import React, { useState, useEffect, useRef, useId } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ScannerIcon,
  PathsModuleIcon,
  JsModuleIcon,
  GitModuleIcon,
  RceModuleIcon,
  MongoDbModuleIcon,
  BackupModuleIcon,
  WafBypassModuleIcon,
  DeepModeIcon,
  MassScanModeIcon,
} from './icons/DashboardIcons';
import {
  buildScanChartPoint,
  buildZeroChartPoint,
  snapshotFromStats,
  formatChartTimeLabel,
  SCAN_CHART_KEYS,
  CHART_STORAGE_KEY,
  ScanChartPoint,
  ScanHitSnapshot,
  EMPTY_SNAPSHOT,
} from '../utils/chartStats';
import { normalizeScanStats } from '../utils/normalizeScanStats';
import { scannerFetch } from '../utils/scannerFetch';
import { readLicenseCounter } from '../utils/licenseDisplay';

const LICENSE_API = process.env.REACT_APP_LICENSE_API || 'https://glitchcloud.app/license-api';
import { isSuperHit } from '../utils/superHit';

interface ChartStorageEntry { data: ScanChartPoint[]; lastCounts: ScanHitSnapshot; }
import './CrackerController.css';
import './AtlasPageLayout.css';
import './ReconDeck.css';

interface CrackerStatus {
  status: string;
  scanning: boolean;
  message: string;
}

interface ScanStats {
  totalUrls: number;
  processedUrls: number;
  hits: number;
  validHitsCount?: number;
  invalidHitsCount?: number;
  superHitsCount?: number;
  awsHits: number;
  sendGridHits: number;
  sparkpostHits: number;
  twilioHits: number;
  brevoHits: number;
  smtpHits: number;
  mailgunHits: number;
  mailchimpHits: number;
  postmarkHits: number;
  sendinblueHits: number;
  stripeHits: number;
  otherHits: number;
  // IA — individuel
  openAiHits?: number;
  anthropicHits?: number;
  googleAiHits?: number;
  huggingFaceHits?: number;
  groqHits?: number;
  cohereHits?: number;
  mistralHits?: number;
  startTime: string;
  eta: string;
  isRunning: boolean;
  urlsPerSecond?: number;
  httpsRequestsPerSecond?: number;
  cpu?: number;
  ram?: number;
  validDomains?: number;
  invalidDomains?: number;
  totalScansLaunched?: number;
  topExploits?: Array<{ name: string; hits: number }>;
  currentFile?: string;
  currentPart?: number;
  totalParts?: number;
  scanLabel?: string;
}

interface Hit {
  id: string;
  url: string;
  path: string;
  services: string[];
  credentials: any;
  discoveredAt: string;
  status: 'valid' | 'invalid';
  isNew: boolean;
  verificationResult?: any;
}

interface CheckFile {
  name: string;
  size: number;
  modified: string;
  path: string;
}

interface VpsEntry { ip: string; port: string }

interface CrackerControllerProps {
  vpsIp?: string;
  vpsPort?: string;
  vpsIp2?: string;   // legacy – consumed via effectiveVpsList
  vpsPort2?: string; // legacy
  vpsIp3?: string;   // legacy – consumed via effectiveVpsList
  vpsPort3?: string; // legacy
  vpsList?: VpsEntry[]; // NEW: all VPS including primary
  isGrabbing?: boolean;
  user?: any;
  license?: any;
  isScanning?: boolean;
  recentHits?: Hit[];
  setActiveTab?: (tab: string) => void;
  domainsCount?: number;
}

interface ExtraSlot {
  scanStats: ScanStats | null;
  status: CrackerStatus | null;
  loading: boolean;
  latencyMs: number | null;
}

function inferApiType(url: string): { label: string; color: string } | null {
  const u = (url || '').toLowerCase();
  if (u.includes('/graphql')) return { label: 'GQL', color: '#e535ab' };
  if (u.includes('/grpc') || u.includes(':50051') || u.includes(':9090')) return { label: 'gRPC', color: '#00add8' };
  if (u.includes('/ws') || u.includes('websocket') || u.startsWith('ws://') || u.startsWith('wss://')) return { label: 'WS', color: '#a78bfa' };
  if (u.includes('/api/') || u.includes('/v1/') || u.includes('/v2/') || u.includes('/v3/') || u.includes('/rest/')) return { label: 'REST', color: '#38bdf8' };
  return null; // HTTP générique — pas de badge
}

function extractApiSnippet(credentials: any, service?: string): string | null {
  if (!credentials || typeof credentials !== 'object') return null;
  const svc = (service || '').toLowerCase().replace(/\s+/g, '');

  // Map service → sous-clé dans credentials
  const SUB: Record<string, string> = {
    sendgrid: 'sendGrid', brevo: 'brevo', sparkpost: 'sparkpost',
    mailgun: 'mailgun', twilio: 'twilio', aws: 'aws',
    smtp: 'smtp', stripe: 'stripe', mailchimp: 'mailchimp',
    postmark: 'postmark',
  };

  const truncate = (v: string) => v.length > 20 ? `${v.slice(0, 16)}···` : v;
  const isCredVal = (v: unknown): v is string =>
    typeof v === 'string' && v.length > 3 && !v.startsWith('http') &&
    v !== 'valid' && v !== 'invalid' && v !== 'unknown';

  const sub = credentials[SUB[svc]];
  if (sub && typeof sub === 'object') {
    // SMTP → montre le host en priorité
    if (svc === 'smtp') {
      if (isCredVal(sub.host)) return sub.host;
      if (isCredVal(sub.username)) return truncate(sub.username);
    }
    // AWS → AKIA (access key ID)
    if (svc === 'aws' && isCredVal(sub.akia)) return truncate(sub.akia);
    // Twilio → accountSid
    if (svc === 'twilio' && isCredVal(sub.accountSid)) return truncate(sub.accountSid);
    // Stripe → secretKey
    if (svc === 'stripe' && isCredVal(sub.secretKey)) return truncate(sub.secretKey);
    // Tous les autres : apiKey en priorité
    for (const f of ['apiKey', 'api_key', 'secretKey', 'token', 'key']) {
      if (isCredVal(sub[f])) return truncate(sub[f]);
    }
  }

  // Services sans sous-objet structuré (OpenAI, GoogleAI, OpenRouter…)
  // → parcourt tous les sous-objets pour trouver n'importe quelle clé
  for (const subObj of Object.values(credentials)) {
    if (!subObj || typeof subObj !== 'object') continue;
    for (const val of Object.values(subObj as object)) {
      if (isCredVal(val)) return truncate(val as string);
    }
  }

  return null;
}

// ── Hit XP system ────────────────────────────────────────────────────────────
function calcHitXP(hit: Hit): number {
  let xp = 0;
  if (hit.status === 'valid') {
    xp += 50;
    xp += (hit.services?.length || 0) * 30;
    if (hit.credentials) {
      const len = JSON.stringify(hit.credentials).length;
      xp += len > 200 ? 80 : len > 50 ? 35 : 10;
    }
    if (hit.verificationResult) xp += 120;
  } else {
    xp += 5;
  }
  // Déterministe jitter depuis l'id
  const id = hit.id || '';
  if (id.length >= 2) {
    xp += (id.charCodeAt(id.length - 1) % 20) + (id.charCodeAt(id.length - 2) % 12);
  }
  return xp;
}

function hitXpTier(xp: number): 'common' | 'rare' | 'epic' | 'legendary' {
  if (xp >= 200) return 'legendary';
  if (xp >= 100) return 'epic';
  if (xp >= 40)  return 'rare';
  return 'common';
}
// ─────────────────────────────────────────────────────────────────────────────

function truncateUrl(url: string, max = 42): string {
  if (!url) return '—';
  try {
    const parsed = new URL(url);
    const hostPath = parsed.hostname + parsed.pathname.replace(/\/$/, '');
    return hostPath.length > max ? hostPath.slice(0, max - 1) + '…' : hostPath;
  } catch {
    return url.length > max ? url.slice(0, max - 1) + '…' : url;
  }
}

const formatCheckFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const CrackerController: React.FC<CrackerControllerProps> = ({ vpsIp, vpsPort, vpsIp2, vpsPort2, vpsIp3, vpsPort3, vpsList, isGrabbing = false, user, license, isScanning = false, recentHits = [], setActiveTab, domainsCount = 0 }) => {
  const [status, setStatus] = useState<CrackerStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scanStats, setScanStats] = useState<ScanStats | null>(null);
  const [showScanAnimation, setShowScanAnimation] = useState(false);
  const [chartData, setChartData] = useState<ScanChartPoint[]>([]);
  const chartInitializedKeyRef = useRef<string | null>(null);
  const prevHitCountsRef       = useRef<ScanHitSnapshot>(EMPTY_SNAPSHOT);
  const wasRunningRef          = useRef(false);
  const lastStartTimeRef       = useRef<string | null>(null);

  // États pour la sélection des fichiers Check
  const [showFileModal, setShowFileModal] = useState(false);
  const [checkFiles, setCheckFiles] = useState<CheckFile[]>([]);
  const [loadingCheckFiles, setLoadingCheckFiles] = useState(false);
  const [selectedCheckFile, setSelectedCheckFile] = useState<string | null>(null);

  // Nouveau modal New Scan (3 étapes)
  const [showNewScanModal, setShowNewScanModal] = useState(false);
  const [newScanStep, setNewScanStep] = useState<1 | 2 | 3>(1);
  const [newScanName, setNewScanName] = useState('');
  const [newScanServerIdx, setNewScanServerIdx] = useState<number>(0);
  const [nsDropdownOpen, setNsDropdownOpen] = useState(false);
  const nsDropdownRef = useRef<HTMLDivElement>(null);
  // scanLabels[serverIdx] = nom du scan lancé sur ce serveur
  const [scanLabels, setScanLabels] = useState<Record<number, string>>({});

  // Nouveaux états pour la configuration du scan
  const [showConfigModal, setShowConfigModal] = useState(false);

  // Pagination Recent Hits
  const [hitsPage, setHitsPage] = useState(0);
  const HITS_PER_PAGE = 5;
  // Reset page when hits change (new scan)
  const prevHitsLenRef = useRef(0);
  useEffect(() => {
    if (recentHits.length !== prevHitsLenRef.current) {
      prevHitsLenRef.current = recentHits.length;
      setHitsPage(0);
    }
  }, [recentHits.length]);
  const [scanMode, setScanMode] = useState<'Deep' | 'Mass'>('Deep');
  const [enablePaths, setEnablePaths] = useState(true);
  const [enableJS, setEnableJS] = useState(true);
  const [enableGIT, setEnableGIT] = useState(true);
  const [enableRCE, setEnableRCE] = useState(true);
  const [enableMongoDB, setEnableMongoDB] = useState(true);
  const [enableBackup, setEnableBackup] = useState(true);
  const [enableWafBypass, setEnableWafBypass] = useState(true);

  // ── États pour le modal Regex Filters ────────────────────────────────────
  const [showRegexModal, setShowRegexModal] = useState(false);
  const [svcEmailOpen, setSvcEmailOpen] = useState(true);
  const [svcAiOpen,    setSvcAiOpen]    = useState(true);
  const [enableRegexAWS,       setEnableRegexAWS]       = useState(true);
  const [enableRegexSendGrid,  setEnableRegexSendGrid]  = useState(true);
  const [enableRegexBrevo,     setEnableRegexBrevo]     = useState(true);
  const [enableRegexMailJet,   setEnableRegexMailJet]   = useState(true);
  const [enableRegexMailGun,   setEnableRegexMailGun]   = useState(true);
  const [enableRegexTencent,   setEnableRegexTencent]   = useState(true);
  const [enableRegexStripe,    setEnableRegexStripe]    = useState(true);
  const [enableRegexSMTP,      setEnableRegexSMTP]      = useState(true);
  const [enableRegexPostMark,  setEnableRegexPostMark]  = useState(true);
  const [enableRegexTwilio,    setEnableRegexTwilio]    = useState(true);
  const [enableRegexSparkpost, setEnableRegexSparkpost] = useState(true);
  const [enableRegexAliyun,    setEnableRegexAliyun]    = useState(true);
  // ── IA — toggle individuel par modèle ────────────────────────────────────
  const [enableRegexOpenAI,        setEnableRegexOpenAI]        = useState(true);
  const [enableRegexAnthropic,     setEnableRegexAnthropic]     = useState(true);
  const [enableRegexGoogleAI,      setEnableRegexGoogleAI]      = useState(true);
  const [enableRegexHuggingFace,   setEnableRegexHuggingFace]   = useState(true);
  const [enableRegexGroq,          setEnableRegexGroq]          = useState(true);
  const [enableRegexCohere,        setEnableRegexCohere]        = useState(true);
  const [enableRegexMistral,       setEnableRegexMistral]       = useState(true);
  const [enableRegexAzureOpenAI,   setEnableRegexAzureOpenAI]   = useState(true);
  const [enableRegexVertexAI,      setEnableRegexVertexAI]      = useState(true);
  const [enableRegexOpenRouter,    setEnableRegexOpenRouter]    = useState(true);
  const [enableRegexOpenCode,      setEnableRegexOpenCode]      = useState(true);
  const [enableRegexMiniMax,       setEnableRegexMiniMax]       = useState(true);

  const [showProxyWarning, setShowProxyWarning] = useState(false);
  const [proxiesCount, setProxiesCount] = useState(0);
  const [proxiesKnown, setProxiesKnown] = useState(false);
  const [laneLatencyMs, setLaneLatencyMs] = useState<number | null>(null);
  const [chartRange, setChartRange] = useState<'1h' | '24h' | '7d'>('1h');
  const [hoveredPieSlice, setHoveredPieSlice] = useState<{ key: string; label: string; value: number; pct: number; color: string; x: number; y: number } | null>(null);
  const pieContainerRef = React.useRef<HTMLDivElement>(null);
  const flowGradId = useId().replace(/:/g, '');
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [publicHitsChoice, setPublicHitsChoice] = useState<boolean | null>(null);
  const [publicHitsPseudo, setPublicHitsPseudo] = useState<string>('');

  // ── Extra VPS slots (index 0 = Server 2, index 1 = Server 3, etc.) ────
  const [extraSlots, setExtraSlots] = useState<ExtraSlot[]>([]);
  const extraSlotsRef = useRef<ExtraSlot[]>([]);
  useEffect(() => { extraSlotsRef.current = extraSlots; }, [extraSlots]);
  // 0-indexed: 0 = Server 1, 1 = Server 2, etc.
  const currentVpsTarget = useRef<number>(0);
  // Prevents concurrent scan launches (double-click, React double-invoke, etc.)
  const scanLaunchInFlight = useRef(false);

  // Build unified VPS list: vpsList wins, then append legacy vpsIp/vpsIp2/vpsIp3 if not covered
  const effectiveVpsList = React.useMemo<VpsEntry[]>(() => {
    let list: VpsEntry[] = [];
    if (vpsList && vpsList.length > 0) {
      list = [...vpsList];
    } else {
      if (vpsIp) list.push({ ip: vpsIp, port: vpsPort || '3007' });
    }
    if (vpsIp2 && !list.some(v => v.ip === vpsIp2)) {
      list.push({ ip: vpsIp2, port: vpsPort2 || '3007' });
    }
    if (vpsIp3 && !list.some(v => v.ip === vpsIp3)) {
      list.push({ ip: vpsIp3, port: vpsPort3 || '3007' });
    }
    return list;
  }, [vpsList, vpsIp, vpsPort, vpsIp2, vpsPort2, vpsIp3, vpsPort3]);

  const patchExtraSlot = React.useCallback((slotIdx: number, patch: Partial<ExtraSlot>) => {
    setExtraSlots(prev => {
      const next = Array.from({ length: Math.max(prev.length, slotIdx + 1) }, (_, i) =>
        prev[i] ?? { scanStats: null, status: null, loading: false, latencyMs: null }
      );
      next[slotIdx] = { ...next[slotIdx], ...patch };
      return next;
    });
  }, []);

  // ── Reset chart complet quand le scan s'arrête ───────────────────────────
  useEffect(() => {
    if (!scanStats) return;
    if (wasRunningRef.current && !scanStats.isRunning) {
      wasRunningRef.current = false;
      setChartData([]);
      prevHitCountsRef.current = EMPTY_SNAPSHOT;
      chartInitializedKeyRef.current = null;
      if (lastStartTimeRef.current) {
        try { localStorage.removeItem(`${CHART_STORAGE_KEY}${lastStartTimeRef.current}`); } catch { /* ignore */ }
        lastStartTimeRef.current = null;
      }
    }
    if (scanStats.isRunning) wasRunningRef.current = true;
  }, [scanStats?.isRunning]);

  // ── Restaurer données graphique depuis localStorage (persistance reconnexion) ──
  useEffect(() => {
    if (!scanStats?.startTime || !scanStats.isRunning) return;
    const startKey = scanStats.startTime;
    lastStartTimeRef.current = startKey;

    if (chartInitializedKeyRef.current && chartInitializedKeyRef.current !== startKey) {
      chartInitializedKeyRef.current = null;
      prevHitCountsRef.current = EMPTY_SNAPSHOT;
    }
    if (chartInitializedKeyRef.current === startKey) return;
    chartInitializedKeyRef.current = startKey;

    const storageKey = `${CHART_STORAGE_KEY}${startKey}`;
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const entry: ChartStorageEntry = JSON.parse(raw);
        if (Array.isArray(entry.data) && entry.data.length > 0) {
          setChartData(entry.data);
          if (entry.lastCounts) prevHitCountsRef.current = entry.lastCounts;
          return;
        }
      }
    } catch { /* ignore */ }

    // Aucune donnée → zero-point à l'heure de démarrage
    // Baseline = snapshot courant pour éviter les anciens hits du scan précédent
    setChartData([buildZeroChartPoint(startKey)]);
    prevHitCountsRef.current = snapshotFromStats(scanStats);
  }, [scanStats?.startTime, scanStats?.isRunning]);

  // Fonctions utilitaires pour les statistiques
  const calculateSpeed = () => {
    if (!scanStats?.startTime) return 0;
    try {
      const startTime = new Date(scanStats.startTime).getTime();
      const now = Date.now();
      const elapsedSeconds = (now - startTime) / 1000;
      if (elapsedSeconds === 0) return 0;
      return Math.round(scanStats.processedUrls / elapsedSeconds);
    } catch (error) {
      return 0;
    }
  };

  const checkStatus = async () => {
    if (!vpsIp || !vpsPort) {
      setError('VPS IP not configured');
      return;
    }

    try {
      const started = performance.now();
      const response = await scannerFetch(vpsIp, vpsPort, '/status');
      const elapsed = Math.round(performance.now() - started);
      if (response.ok) {
        const data = await response.json();
        setStatus(data);
        setLaneLatencyMs(elapsed);
        setError(null);
      } else {
        setLaneLatencyMs(null);
        setError('Could not reach the scanner service');
      }
    } catch (err) {
      setLaneLatencyMs(null);
      setError('Scanner unavailable');
    }
  };

  const fetchStats = async () => {
    if (!vpsIp || !vpsPort) return;

    try {
      const response = await scannerFetch(vpsIp, vpsPort, '/realtime-stats');
      if (response.ok) {
        const data = await response.json();
        // Utiliser la même logique que StatsDisplay
        if (data.success) {
          const normalized = normalizeScanStats(data.stats);
          setScanStats(normalized);
          // N'ajouter un point que si le scan tourne ET que l'init est faite
          if (normalized?.isRunning && chartInitializedKeyRef.current) {
            const prev      = prevHitCountsRef.current;
            const newPoint  = buildScanChartPoint(normalized, formatChartTimeLabel(), prev);
            // Add extra-slot speeds so the chart reflects the combined fleet throughput
            const extraSpeedSum = extraSlotsRef.current.reduce((s, slot) => {
              const spd = slot.scanStats?.urlsPerSecond;
              return s + (typeof spd === 'number' ? Math.round(spd) : 0);
            }, 0);
            newPoint.speed += extraSpeedSum;
            const newCounts = snapshotFromStats(normalized);
            prevHitCountsRef.current = newCounts;

            setChartData((prevData) => {
              const last = prevData[prevData.length - 1];

              // Déduplication : même label de temps → ignorer
              if (last?.time === newPoint.time) return prevData;

              // Creux de connectivité : chute > 50 % de processedUrls → point invalide
              const lastProcessed = typeof last?.processedUrls === 'number' ? last.processedUrls : 0;
              if (
                prevData.length > 1 &&
                lastProcessed > 10 &&
                newPoint.processedUrls < lastProcessed * 0.5
              ) return prevData;

              const updated = [...prevData, newPoint].slice(-500);
              if (normalized?.startTime) {
                const sk = `${CHART_STORAGE_KEY}${normalized.startTime}`;
                const entry: ChartStorageEntry = { data: updated, lastCounts: newCounts };
                try { localStorage.setItem(sk, JSON.stringify(entry)); } catch { /* ignore */ }
              }
              return updated;
            });
          }
        }
        // data.success === false : erreur transitoire — on garde les dernières données
      }
      // !response.ok : on garde les dernières données
    } catch (err) {
      console.error('Erreur récupération stats (transitoire — données conservées):', err);
      // Ne pas effacer scanStats : on garde les dernières valeurs connues
    }
  };

  const hasActiveModule =
    enablePaths || enableJS || enableGIT || enableRCE ||
    enableMongoDB || enableBackup || enableWafBypass;

  const loadCheckFiles = async () => {
    const activeVps = effectiveVpsList[currentVpsTarget.current];
    const targetIp = activeVps?.ip || vpsIp;
    const targetPort = activeVps?.port || vpsPort;
    if (!targetIp || !targetPort) {
      setError('VPS IP not configured');
      return;
    }

    try {
      setLoadingCheckFiles(true);
      const response = await scannerFetch(targetIp, targetPort, '/check-files');

      if (response.ok) {
        const data = await response.json();
        const files: CheckFile[] = data.files || [];
        setCheckFiles(files);
        setSelectedCheckFile(files.length > 0 ? files[0].name : null);
        setError(null);
      } else {
        setError('Could not load Check files');
        setCheckFiles([]);
        setSelectedCheckFile(null);
      }
    } catch {
      setError('Could not load Check files');
      setCheckFiles([]);
      setSelectedCheckFile(null);
    } finally {
      setLoadingCheckFiles(false);
    }
  };

  const selectCheckFile = (fileName: string) => {
    setSelectedCheckFile(fileName);
  };

  const formatDisplayPseudo = (value?: string | null) => {
    if (!value) return '';
    const trimmed = value.trim();
    if (!trimmed || trimmed.toLowerCase() === 'anonymous') return '';
    return trimmed.startsWith('@') ? trimmed : `@${trimmed}`;
  };

  const resolveOperatorPseudo = (serverPseudo?: string, originalPseudo?: string) => {
    return (
      formatDisplayPseudo(originalPseudo) ||
      formatDisplayPseudo(serverPseudo) ||
      formatDisplayPseudo(license?.username) ||
      formatDisplayPseudo(user?.name) ||
      'votre @'
    );
  };

  const loadPublicHitsPseudo = async () => {
    const fallback = resolveOperatorPseudo();
    setPublicHitsPseudo(fallback);

    if (!vpsIp || !vpsPort) return;

    try {
      const response = await scannerFetch(vpsIp, vpsPort, '/telegram-config');

      if (!response.ok) return;

      const config = await response.json();
      setPublicHitsPseudo(
        resolveOperatorPseudo(config.userPseudo, config.originalUserPseudo)
      );
    } catch {
      setPublicHitsPseudo(fallback);
    }
  };

  const handleContinueToConfig = () => {
    if (!selectedCheckFile) {
      setError('Please select a Check file');
      return;
    }
    setError(null);
    setShowFileModal(false);
    setShowRegexModal(true);
  };

  const handleContinueToRegex = () => {
    setError(null);
    setShowRegexModal(true);
  };

  const handleContinueToPrivacy = () => {
    setError(null);
    setShowRegexModal(false);
    startScan();
  };

  const executeScan = async () => {
    if (scanLaunchInFlight.current) return;
    scanLaunchInFlight.current = true;

    const activeVps = effectiveVpsList[currentVpsTarget.current];
    const activeVpsIp = activeVps?.ip || vpsIp;
    const activeVpsPort = activeVps?.port || vpsPort;
    if (!activeVpsIp || !activeVpsPort) {
      scanLaunchInFlight.current = false;
      setError('VPS IP not configured');
      return;
    }

    if (isGrabbing) {
      setError('Cannot start a scan while the grabber is running');
      return;
    }

    // Verify qu'au moins une option est enabled
    if (!hasActiveModule) {
      setError('At least one option must be enabled (Paths, JS, GIT, RCE, MongoDB, BackUP, or WAF bypass)');
      return;
    }

    if (!selectedCheckFile) {
      setError('Please select a Check file');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const requestBody = {
        selectedFiles: [selectedCheckFile],
        mode: 'Deep',
        enablePaths,
        enableJS,
        enableGIT,
        enableRCE,
        enableMongoDB,
        enableBackup,
        enableWafBypass,
        // Regex par service
        enableRegexAWS,
        enableRegexSendGrid,
        enableRegexBrevo,
        enableRegexMailJet,
        enableRegexMailGun,
        enableRegexTencent,
        enableRegexStripe,
        enableRegexSMTP,
        enableRegexPostMark,
        enableRegexTwilio,
        enableRegexSparkpost,
        enableRegexAliyun,
        // IA — individuel
        enableRegexOpenAI,
        enableRegexAnthropic,
        enableRegexGoogleAI,
        enableRegexHuggingFace,
        enableRegexGroq,
        enableRegexCohere,
        enableRegexMistral,
        // Nouvelles IA
        enableRegexAzureOpenAI,
        enableRegexVertexAI,
        enableRegexOpenRouter,
        enableRegexOpenCode,
        enableRegexMiniMax,
        enableRegexResend: true,
        showPublicHits: true,
        userPseudo: publicHitsPseudo,
      };

      console.log('Envoi des paramètres de scan:', requestBody);
      
      const response = await scannerFetch(activeVpsIp, activeVpsPort, '/start-scan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Scan démarré:', data.message);

        // Incrémenter le compteur de scans sur la licence
        const _jwt = (license as any)?.jwt;
        const _code = (license as any)?.code;
        if (_jwt && _code) {
          fetch(`${LICENSE_API}/scan/increment`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${_jwt}` },
            body: JSON.stringify({ license: _code }),
          }).catch(() => {});
        }

        // Close les modals
        setShowFileModal(false);
        setShowConfigModal(false);
        setShowRegexModal(false);
        
        // Afficher l'animation de lancement
        setShowScanAnimation(true);
        setTimeout(() => setShowScanAnimation(false), 4000);

        // Verify le statut après un délai
        if (currentVpsTarget.current === 0) {
          setTimeout(checkStatus, 2000);
        } else {
          const tgt = currentVpsTarget.current;
          setTimeout(() => checkExtraStatus(tgt), 2000);
        }
      } else {
        let errorMsg = `HTTP ${response.status}`;
        try {
          const errorData = await response.json();
          errorMsg = errorData.error || errorData.detail || errorMsg;
        } catch { /* non-JSON response — keep HTTP status */ }
        setError(errorMsg);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to start scan');
    } finally {
      setLoading(false);
      scanLaunchInFlight.current = false;
    }
  };

  const startScan = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Verify d'abord les proxies
      try {
        const _activeVps = effectiveVpsList[currentVpsTarget.current];
        const proxiesResponse = await scannerFetch(
          _activeVps?.ip || vpsIp || '', _activeVps?.port || vpsPort || '', '/proxies',
        );
        
        if (proxiesResponse.ok) {
          const proxiesData = await proxiesResponse.json();
          const count = proxiesData.totalCount || proxiesData.proxies?.length || 0;
          setProxiesCount(count);
          setProxiesKnown(true);
          
          if (count === 0) {
            setShowProxyWarning(true);
            setLoading(false);
            return;
          }
        }
      } catch (proxyErr) {
        console.error('Erreur vérification proxies:', proxyErr);
        // Continue même si la vérification échoue
      }
      
      // Si les proxies sont configurés, lancer le scan
      await executeScan();
    } catch (err) {
      setError('Unable to start scan');
      setLoading(false);
    }
  };
  
  // ── Nouveau modal New Scan ──────────────────────────────────────────────────
  const openNewScanModal = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    e?.stopPropagation();
    setNewScanName('');
    setNewScanServerIdx(0);
    setSelectedCheckFile(null);
    setCheckFiles([]); // clear stale files — will load fresh when server is chosen in step 1
    setNewScanStep(1);
    setShowNewScanModal(true);
    loadPublicHitsPseudo();
  };

  const closeNewScanModal = () => {
    setShowNewScanModal(false);
    setNewScanStep(1);
    setNewScanName('');
    setSelectedCheckFile(null);
  };

  const launchNewScan = async () => {
    if (scanLaunchInFlight.current) return;
    scanLaunchInFlight.current = true;
    const activeVps = effectiveVpsList[newScanServerIdx];
    if (!activeVps) { scanLaunchInFlight.current = false; return; }
    if (!selectedCheckFile) { scanLaunchInFlight.current = false; setError('Please select a Check file'); return; }
    currentVpsTarget.current = newScanServerIdx;

    setLoading(true);
    setError(null);

    try {
      // Vérif proxies
      try {
        const proxiesResponse = await scannerFetch(activeVps.ip, activeVps.port, '/proxies');
        if (proxiesResponse.ok) {
          const proxiesData = await proxiesResponse.json();
          const count = proxiesData.totalCount || proxiesData.proxies?.length || 0;
          setProxiesCount(count);
          setProxiesKnown(true);
          if (count === 0) {
            closeNewScanModal();
            setShowProxyWarning(true);
            setLoading(false);
            return;
          }
        }
      } catch { /* continue */ }

      const requestBody = {
        selectedFiles: [selectedCheckFile],
        mode: 'Deep',
        enablePaths: true, enableJS: true, enableGIT: true,
        enableRCE: true, enableMongoDB: true, enableBackup: true, enableWafBypass: true,
        enableRegexAWS: true, enableRegexSendGrid: true, enableRegexBrevo: true,
        enableRegexMailJet: true, enableRegexMailGun: true, enableRegexTencent: true,
        enableRegexStripe: true, enableRegexSMTP: true, enableRegexPostMark: true,
        enableRegexTwilio: true, enableRegexSparkpost: true, enableRegexAliyun: true,
        enableRegexOpenAI: true, enableRegexAnthropic: true, enableRegexGoogleAI: true,
        enableRegexHuggingFace: true, enableRegexGroq: true, enableRegexCohere: true,
        enableRegexMistral: true, enableRegexAzureOpenAI: true, enableRegexVertexAI: true,
        enableRegexOpenRouter: true, enableRegexOpenCode: true, enableRegexMiniMax: true,
        enableRegexResend: true,
        showPublicHits: true,
        userPseudo: publicHitsPseudo || '',
        scanLabel: newScanName.trim() || undefined,
      };

      const response = await scannerFetch(activeVps.ip, activeVps.port, '/start-scan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        // Incrémenter le compteur de scans sur la licence
        const _jwt2 = (license as any)?.jwt;
        const _code2 = (license as any)?.code;
        if (_jwt2 && _code2) {
          fetch(`${LICENSE_API}/scan/increment`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${_jwt2}` },
            body: JSON.stringify({ license: _code2 }),
          }).catch(() => {});
        }

        // Stocker le nom du scan par serveur
        const label = newScanName.trim();
        if (label) setScanLabels(prev => ({ ...prev, [newScanServerIdx]: label }));
        closeNewScanModal();
        setShowScanAnimation(true);
        setTimeout(() => setShowScanAnimation(false), 4000);
        if (newScanServerIdx === 0) {
          setTimeout(checkStatus, 2000);
        } else {
          const tgt = newScanServerIdx;
          setTimeout(() => checkExtraStatus(tgt), 2000);
        }
      } else {
        let errMsg = `HTTP ${response.status}`;
        try {
          const errData = await response.json();
          errMsg = errData.error || errData.detail || errMsg;
        } catch { /* non-JSON response */ }
        setError(errMsg);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to start scan');
    } finally {
      setLoading(false);
      scanLaunchInFlight.current = false;
    }
  };

  const handleStartClick = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    e?.stopPropagation();
    // Direct launch for Server 1 — skip server selection, load Server 1 files
    currentVpsTarget.current = 0;
    setPublicHitsChoice(null);
    setShowPrivacyModal(false);
    setShowRegexModal(false);
    setShowConfigModal(false);
    setSelectedCheckFile(null);
    setShowFileModal(true);
    loadCheckFiles();
  };

  // Generic handler for extra VPS (vpsIdx = 1-based server index, e.g. 1 = Server 2)
  const handleStartClickN = (vpsIdx: number) => (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    e?.stopPropagation();
    const slotIdx = vpsIdx - 1;
    if (extraSlots[slotIdx]?.status?.scanning) {
      stopExtraScan(vpsIdx);
      return;
    }
    currentVpsTarget.current = vpsIdx;
    setPublicHitsChoice(null);
    setShowPrivacyModal(false);
    setShowRegexModal(false);
    setShowConfigModal(false);
    setSelectedCheckFile(null);
    setShowFileModal(true);
    loadCheckFiles();
  };

  const checkExtraStatus = async (vpsIdx: number) => {
    const vps = effectiveVpsList[vpsIdx];
    if (!vps) return;
    try {
      const t0 = performance.now();
      const res = await scannerFetch(vps.ip, vps.port, '/status');
      const latencyMs = Math.round(performance.now() - t0);
      if (res.ok) patchExtraSlot(vpsIdx - 1, { status: await res.json(), latencyMs });
      else patchExtraSlot(vpsIdx - 1, { latencyMs: null });
    } catch { patchExtraSlot(vpsIdx - 1, { latencyMs: null }); }
  };

  const fetchExtraStats = async (vpsIdx: number) => {
    const vps = effectiveVpsList[vpsIdx];
    if (!vps) return;
    try {
      const res = await scannerFetch(vps.ip, vps.port, '/realtime-stats');
      if (res.ok) {
        const data = await res.json();
        if (data.success) patchExtraSlot(vpsIdx - 1, { scanStats: normalizeScanStats(data.stats) });
        // data.success === false : transitoire — conserver les dernières stats
      }
      // !res.ok : conserver les dernières stats
    } catch { /* transitoire — conserver les dernières stats */ }
  };

  const stopExtraScan = async (vpsIdx: number) => {
    const vps = effectiveVpsList[vpsIdx];
    if (!vps) return;
    patchExtraSlot(vpsIdx - 1, { loading: true });
    setError(null);
    try {
      const res = await scannerFetch(vps.ip, vps.port, '/stop-scan', { method: 'POST' });
      if (res.ok) {
        setScanLabels(prev => { const n = { ...prev }; delete n[vpsIdx]; return n; });
        setTimeout(() => checkExtraStatus(vpsIdx), 1000);
      }
    } catch { setError(`Could not stop Server ${vpsIdx + 1} scan`); }
    finally { patchExtraSlot(vpsIdx - 1, { loading: false }); }
  };

  const stopScan = async () => {
    if (!vpsIp || !vpsPort) {
      setError('VPS IP not configured');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const response = await scannerFetch(vpsIp, vpsPort, '/stop-scan', { method: 'POST' });

      if (response.ok) {
        const data = await response.json();
        console.log('Scan arrêté:', data.message);
        setScanLabels(prev => { const n = { ...prev }; delete n[0]; return n; });
        setTimeout(checkStatus, 1000);
      } else {
        setError('Error stopping scan');
      }
    } catch (err) {
      setError('Could not stop the scan');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkStatus();
    fetchStats();
    const statusInterval = setInterval(checkStatus, 5000);
    const statsInterval = setInterval(fetchStats, 5000);
    return () => {
      clearInterval(statusInterval);
      clearInterval(statsInterval);
    };
  }, []);

  // Poll all extra VPS slots (index 1+) — re-runs when the VPS list changes
  const extraVpsKey = effectiveVpsList.slice(1).map(v => `${v.ip}:${v.port}`).join('|');
  useEffect(() => {
    const extra = effectiveVpsList.slice(1);
    if (extra.length === 0) return;
    const timers: ReturnType<typeof setInterval>[] = [];
    extra.forEach((vps, i) => {
      const doStatus = async () => {
        try {
          const t0 = performance.now();
          const res = await scannerFetch(vps.ip, vps.port, '/status');
          const latencyMs = Math.round(performance.now() - t0);
          if (res.ok) patchExtraSlot(i, { status: await res.json(), latencyMs });
          else patchExtraSlot(i, { latencyMs: null });
        } catch { patchExtraSlot(i, { latencyMs: null }); }
      };
      const doStats = async () => {
        try {
          const res = await scannerFetch(vps.ip, vps.port, '/realtime-stats');
          if (res.ok) {
            const data = await res.json();
            if (data.success) patchExtraSlot(i, { scanStats: normalizeScanStats(data.stats) });
          }
        } catch { /* transitoire — conserver les dernières stats */ }
      };
      doStatus();
      doStats();
      timers.push(setInterval(doStatus, 5000));
      timers.push(setInterval(doStats, 2000));
    });
    return () => timers.forEach(clearInterval);
  }, [extraVpsKey]); // eslint-disable-line

  useEffect(() => {
    if (!vpsIp || !vpsPort) return;
    let cancelled = false;
    const loadProxies = async () => {
      try {
        const proxiesResponse = await scannerFetch(vpsIp, vpsPort, '/proxies');
        if (!proxiesResponse.ok) return;
        const proxiesData = await proxiesResponse.json();
        const count = proxiesData.totalCount ?? proxiesData.proxies?.length;
        if (!cancelled && typeof count === 'number') {
          setProxiesCount(count);
          setProxiesKnown(true);
        }
      } catch {
        /* proxy inventory unavailable */
      }
    };
    loadProxies();
    const timer = setInterval(loadProxies, 15000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [vpsIp, vpsPort]);

  const calculateAccuracyRate = () => {
    if (!scanStats) return 0;
    
    // Utiliser le total des URLs si processedUrls est 0 ou très petit
    const totalProcessed = scanStats.processedUrls > 0 ? scanStats.processedUrls : scanStats.totalUrls;
    
    if (totalProcessed === 0) return 0;
    
    const rate = (scanStats.hits / totalProcessed) * 100;
    
    // Si le taux est très petit (< 0.01%), afficher au moins 0.01%
    if (rate > 0 && rate < 0.01) {
      return 0.01;
    }
    
    return Math.round(rate * 100) / 100; // Arrondir à 2 décimales
  };

  const formatDuration = () => {
    if (!scanStats?.startTime) return 'N/A';
    try {
      const start = new Date(scanStats.startTime).getTime();
      const now = Date.now();
      const diff = now - start;
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      return `${hours}h ${minutes}m ${seconds}s`;
    } catch (error) {
      return 'N/A';
    }
  };

  const scanProgress = scanStats && scanStats.totalUrls > 0
    ? Math.round((scanStats.processedUrls / scanStats.totalUrls) * 100)
    : 0;

  // Les event-dots n'ont pas besoin de scale visuel (toujours 0|1)

  const isScanActive = !!(isScanning || status?.scanning);

  const urlsPerSec = scanStats?.isRunning
    ? (typeof scanStats?.urlsPerSecond === 'number' ? Math.round(scanStats.urlsPerSecond) : calculateSpeed())
    : 0;

  const classificationReported =
    !!scanStats &&
    (typeof scanStats.validHitsCount === 'number' ||
      typeof scanStats.invalidHitsCount === 'number' ||
      typeof scanStats.superHitsCount === 'number');

  const validN = typeof scanStats?.validHitsCount === 'number' ? Math.max(0, scanStats.validHitsCount) : 0;
  const invalidN = typeof scanStats?.invalidHitsCount === 'number' ? Math.max(0, scanStats.invalidHitsCount) : 0;
  const superN = typeof scanStats?.superHitsCount === 'number' ? Math.max(0, scanStats.superHitsCount) : 0;
  const hitTotal = scanStats ? Math.max(0, scanStats.hits) : 0;
  const brutsN = Math.max(0, hitTotal - validN - invalidN - superN);

  // Database-based counts derived from the actual stored hits (recentHits prop)
  const DB_BRUT_RE = /timeout|cancel|bad.?request|400|unauthorized|401|rate.?limit|429|refused|econnrefused|enotfound|network.?error/i;
  const dbBrutsN = (recentHits ?? []).filter(h => {
    if (h.status === 'valid' && !h.verificationResult) return true;
    if (h.status === 'invalid') {
      const str = typeof h.verificationResult === 'string'
        ? h.verificationResult
        : JSON.stringify(h.verificationResult ?? '');
      return DB_BRUT_RE.test(str);
    }
    return false;
  }).length;
  const dbSpecialN = (recentHits ?? []).filter(h => isSuperHit(h)).length;

  const formatCount = (value: number) =>
    Math.trunc(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

  const processedForRate = scanStats?.processedUrls ?? 0;
  const hitRateLabel =
    scanStats && processedForRate > 0
      ? `${((hitTotal / processedForRate) * 100).toFixed(2)}%`
      : '—';

  const awsRandomCount = (recentHits ?? []).filter(h =>
    (h.services ?? []).some(s => {
      const k = s.toLowerCase();
      if (k !== 'smtp' && k !== 'mail' && k !== 'mailer') return false;
      const creds = h.credentials?.[s] ?? h.credentials?.[s.toLowerCase()] ?? h.credentials ?? {};
      const user = creds?.username || creds?.user || creds?.email || '';
      return typeof user === 'string' && /^AKIA[A-Z0-9]{12,}/.test(user.trim());
    })
  ).length;


  const formatHitAge = (iso: string) => {
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return '';
    const sec = Math.max(0, Math.floor((Date.now() - then) / 1000));
    if (sec < 60) return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hours = Math.floor(min / 60);
    return `${hours}h ${min % 60}m ago`;
  };

  const rangeMs = chartRange === '1h' ? 60 * 60 * 1000 : chartRange === '24h' ? 24 * 60 * 60 * 1000 : 7 * 24 * 60 * 60 * 1000;
  const nowMs = Date.now();
  const rangedPoints = chartData.filter((point) => {
    const ts = typeof point.ts === 'number' ? point.ts : null;
    if (ts == null) return false;
    return nowMs - ts <= rangeMs;
  });
  const reqPerUrl = 359;
  const reqPerSec = urlsPerSec * reqPerUrl;

  // ── Per-slot computed values (for N extra VPS) ────────────────────────
  const slotMetrics = extraSlots.map(slot => {
    const s = slot.scanStats;
    const ups = (s?.isRunning && typeof s?.urlsPerSecond === 'number') ? Math.round(s.urlsPerSecond) : 0;
    const rps = ups * reqPerUrl;
    const hits = s ? Math.max(0, s.hits) : 0;
    const vld = typeof s?.validHitsCount === 'number' ? Math.max(0, s.validHitsCount) : 0;
    const inv = typeof s?.invalidHitsCount === 'number' ? Math.max(0, s.invalidHitsCount) : 0;
    const sup = typeof s?.superHitsCount === 'number' ? Math.max(0, s.superHitsCount) : 0;
    const brut = Math.max(0, hits - vld - inv - sup);
    const cpu = s?.cpu ?? 0;
    const ram = s?.ram ?? 0;
    const vdoms = s?.isRunning ? (s.validDomains ?? 0) : 0;
    const idoms = s?.isRunning ? (s.invalidDomains ?? 0) : 0;
    const tdoms = vdoms + idoms;
    const vdomPct = tdoms > 0 ? Math.round((vdoms / tdoms) * 100) : 0;
    const prog = s && s.totalUrls > 0 ? Math.round((s.processedUrls / s.totalUrls) * 100) : 0;
    return {
      scanStats: s, status: slot.status, loading: slot.loading,
      urlsPerSec: ups, reqPerSec: rps, hitTotal: hits,
      validN: vld, invalidN: inv, superN: sup, brutsN: brut,
      cpuVal: cpu, ramVal: ram,
      cpuColor: cpu > 80 ? '#FF3B55' : cpu > 50 ? '#F59E0B' : '#00E87A',
      ramColor: ram > 80 ? '#FF3B55' : ram > 50 ? '#F59E0B' : '#00E87A',
      validDoms: vdoms, invalidDoms: idoms, totalDoms: tdoms,
      validDomPct: vdomPct, invalidDomPct: tdoms > 0 ? 100 - vdomPct : 0,
      scanProgress: prog,
    };
  });

  // Combined totals across ALL configured VPS
  const combinedHitTotal = hitTotal + slotMetrics.reduce((s, m) => s + m.hitTotal, 0);
  const combinedReqPerSec = reqPerSec + slotMetrics.reduce((s, m) => s + m.reqPerSec, 0);
  const combinedUrlsPerSec = urlsPerSec + slotMetrics.reduce((s, m) => s + m.urlsPerSec, 0);
  const combinedValidN = validN + slotMetrics.reduce((s, m) => s + m.validN, 0);
  const combinedInvalidN = invalidN + slotMetrics.reduce((s, m) => s + m.invalidN, 0);
  const combinedSuperN = superN + slotMetrics.reduce((s, m) => s + m.superN, 0);
  const combinedBrutsN = Math.max(0, combinedHitTotal - combinedValidN - combinedInvalidN - combinedSuperN);

  // Aggregate service hits across ALL active scan stats (Server 1 + all extra slots)
  const allActiveStats = [scanStats, ...slotMetrics.map(m => m.scanStats)].filter(Boolean) as ScanStats[];
  const sumSvc = (key: keyof ScanStats) =>
    allActiveStats.reduce((s, st) => s + ((st[key] as number) || 0), 0);
  const serviceRows = (allActiveStats.length > 0
    ? [
        { label: 'AWS',       value: sumSvc('awsHits'),       color: '#60a5fa' },
        { label: 'AWS RANDOM',value: awsRandomCount,          color: '#f59e0b' },
        { label: 'SendGrid',  value: sumSvc('sendGridHits'),  color: '#38bdf8' },
        { label: 'SMTP',      value: sumSvc('smtpHits'),      color: '#00E87A' },
        { label: 'Brevo',     value: sumSvc('brevoHits'),     color: '#a3e635' },
        { label: 'Mailgun',   value: sumSvc('mailgunHits'),   color: '#facc15' },
        { label: 'Mailchimp', value: sumSvc('mailchimpHits'), color: '#fb923c' },
        { label: 'Postmark',  value: sumSvc('postmarkHits'),  color: '#f97316' },
        { label: 'Twilio',    value: sumSvc('twilioHits'),    color: '#f43f5e' },
        { label: 'SparkPost', value: sumSvc('sparkpostHits'), color: '#e879f9' },
        { label: 'Stripe',    value: sumSvc('stripeHits'),    color: '#c084fc' },
        { label: 'OpenAI',    value: sumSvc('openAiHits'),    color: '#34d399' },
        { label: 'Anthropic', value: sumSvc('anthropicHits'), color: '#fbbf24' },
        { label: 'Google AI', value: sumSvc('googleAiHits'),  color: '#22d3ee' },
        { label: 'Other',     value: sumSvc('otherHits'),     color: '#94a3b8' },
      ]
    : []
  )
    .filter((row) => row.value > 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, 10);
  const serviceMax = serviceRows[0]?.value || 1;
  const chartMax = rangedPoints.reduce((max, point) => Math.max(max, (point.speed || 0) * reqPerUrl), 0);
  const chartLine = rangedPoints
    .map((point, index) => {
      const x = rangedPoints.length === 1 ? 0 : (index / (rangedPoints.length - 1)) * 100;
      const y = chartMax > 0 ? 100 - (((point.speed || 0) * reqPerUrl) / chartMax) * 88 - 6 : 94;
      return `${index === 0 ? 'M' : 'L'}${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .join(' ');
  const chartArea = chartLine ? `${chartLine} L100 100 L0 100 Z` : '';
  const lastPoint = rangedPoints[rangedPoints.length - 1];
  const chartDotX = rangedPoints.length <= 1 ? 0 : 100;
  const chartDotY = lastPoint && chartMax > 0 ? 100 - (((lastPoint.speed || 0) * reqPerUrl) / chartMax) * 88 - 6 : 94;
  const reqPerMin = urlsPerSec * 60;
  const licenseRecord = (license ?? {}) as Record<string, unknown>;
  const combinedScansLaunched =
    (scanStats?.totalScansLaunched ?? 0) +
    slotMetrics.reduce((s, m) => s + (m.scanStats?.totalScansLaunched ?? 0), 0);
  const totalScans = combinedScansLaunched > 0
    ? combinedScansLaunched
    : readLicenseCounter(licenseRecord, ['totalScans', 'totalScan', 'scansTotal', 'totalScansLaunched']);
  const licenseLevel = (license as any)?.currentLevel ?? readLicenseCounter(licenseRecord, ['currentLevel', 'level', 'lvl', 'scanLevel']);
  const currentLevelXp: number = (license as any)?.currentLevelXp ?? 0;
  const nextLevelXp: number = (license as any)?.nextLevelXp ?? 0;
  const xpPct = nextLevelXp > 0 ? Math.min(100, Math.round((currentLevelXp / nextLevelXp) * 100)) : 0;
  const serviceColor = (label: string) => {
    const key = label.toLowerCase();
    if (key.includes('aws')) return '#3b82f6';
    if (key.includes('google')) return '#4285f4';
    if (key.includes('stripe')) return '#a855f7';
    if (key.includes('smtp')) return '#f59e0b';
    if (key.includes('openai') || key.includes('open ai')) return '#14b8a6';
    if (key.includes('send')) return '#f97316';
    if (key.includes('twilio')) return '#f43f5e';
    if (key.includes('brevo') || key.includes('sendinblue')) return '#0ea5e9';
    if (key.includes('mailgun')) return '#e879f9';
    if (key.includes('anthropic')) return '#d97706';
    if (key.includes('openrouter')) return '#8b5cf6';
    return '#94a3b8'; // gris neutre par défaut (jamais vert)
  };

  const remainingUrls = scanStats ? Math.max(0, scanStats.totalUrls - scanStats.processedUrls) : null;

  const cpuVal = scanStats?.cpu ?? 0;
  const ramVal = scanStats?.ram ?? 0;
  const cpuColor = cpuVal > 80 ? '#FF3B55' : cpuVal > 50 ? '#F59E0B' : '#00E87A';
  const _activeCpus = [
    ...(scanStats || status ? [cpuVal] : []),
    ...slotMetrics.filter(m => m.scanStats || m.status).map(m => m.cpuVal),
  ];
  const avgCpu = _activeCpus.length ? Math.round(_activeCpus.reduce((a, b) => a + b, 0) / _activeCpus.length) : 0;
  const avgCpuColor = avgCpu > 80 ? '#FF3B55' : avgCpu > 50 ? '#F59E0B' : avgCpu > 25 ? '#00E87A' : '#3B82F6';
  const ramColor = ramVal > 80 ? '#FF3B55' : ramVal > 50 ? '#F59E0B' : '#00E87A';

  const validDoms = (scanStats?.isRunning ? (scanStats.validDomains ?? 0) : 0)
    + slotMetrics.reduce((s, m) => s + (m.scanStats?.isRunning ? (m.scanStats.validDomains ?? 0) : 0), 0);
  const invalidDoms = (scanStats?.isRunning ? (scanStats.invalidDomains ?? 0) : 0)
    + slotMetrics.reduce((s, m) => s + (m.scanStats?.isRunning ? (m.scanStats.invalidDomains ?? 0) : 0), 0);
  const totalDoms = validDoms + invalidDoms;
  const validDomPct = totalDoms > 0 ? Math.round((validDoms / totalDoms) * 100) : 0;
  const invalidDomPct = totalDoms > 0 ? 100 - validDomPct : 0;

  const userRank: string = (license as any)?.rank || (
    (() => {
      const lvl = (license as any)?.currentLevel ?? 1;
      if (lvl >= 100) return 'Injected';
      if (lvl >= 24) return 'Breached';
      return 'Ghosted';
    })()
  );

  const _pieRaw = serviceRows
    .filter(r => r.value > 0)
    .map(r => ({ key: r.label, label: r.label, value: r.value, color: serviceColor(r.label) }));
  const _pieTotalRaw = _pieRaw.reduce((s, sl) => s + sl.value, 0);
  /* Minimum visible slice = 3% of circle. Small slices get bumped up;
     large slices are scaled down proportionally to compensate. */
  const MIN_SLICE = 0.03;
  const pieSlices: Array<{ key: string; label: string; value: number; color: string; visual: number }> =
    _pieTotalRaw > 0 ? (() => {
      const smallCount = _pieRaw.filter(s => s.value / _pieTotalRaw < MIN_SLICE).length;
      const reservedFrac = smallCount * MIN_SLICE;
      const freeFrac = Math.max(0, 1 - reservedFrac);
      const largeSum = _pieRaw
        .filter(s => s.value / _pieTotalRaw >= MIN_SLICE)
        .reduce((a, s) => a + s.value, 0);
      return _pieRaw.map(s => {
        const nat = s.value / _pieTotalRaw;
        const visual = nat < MIN_SLICE
          ? MIN_SLICE
          : largeSum > 0 ? (s.value / largeSum) * freeFrac : nat;
        return { ...s, visual };
      });
    })() : [];
  const pieTotal = _pieTotalRaw;
  const pieR = 36;
  const pieC = 2 * Math.PI * pieR;
  let pieCursor = 0;

  const operatorName = user?.name || license?.username || 'operator';

  return (
    <div className="deck-page cracker-shell cracker-shell--atlas">
      <AnimatePresence>
        {showScanAnimation && (
          <motion.div
            className="sl-hud"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.6 } }}
          >
            {/* Radial rings */}
            <div className="sl-rings">
              <div className="sl-ring sl-ring--1" />
              <div className="sl-ring sl-ring--2" />
              <div className="sl-ring sl-ring--3" />
            </div>
            {/* Core icon */}
            <motion.div
              className="sl-core"
              initial={{ scale: 0.4, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 260, damping: 18, delay: 0.1 }}
            >
              <svg className="sl-bolt" viewBox="0 0 24 24" fill="none">
                <path d="M13 2L4.5 13.5H11L10 22L19.5 10H13L13 2Z" fill="url(#sl-bolt-grad)" stroke="none"/>
                <defs>
                  <linearGradient id="sl-bolt-grad" x1="4" y1="2" x2="20" y2="22" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#00E87A"/>
                    <stop offset="1" stopColor="#00bcd4"/>
                  </linearGradient>
                </defs>
              </svg>
            </motion.div>
            {/* Text block */}
            <motion.div
              className="sl-text"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.28, duration: 0.4 }}
            >
              <p className="sl-label">SCAN INITIATED</p>
              <h3 className="sl-title">Sequence deployed</h3>
              <p className="sl-sub">Scanner is now active on your dedicated environment</p>
            </motion.div>
            {/* Progress bar */}
            <motion.div
              className="sl-bar-wrap"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <div className="sl-bar-track">
                <motion.div
                  className="sl-bar-fill"
                  initial={{ width: '0%' }}
                  animate={{ width: '100%' }}
                  transition={{ duration: 3.2, ease: 'linear', delay: 0.5 }}
                />
              </div>
              <span className="sl-bar-label">Initialising…</span>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="recon">
      <header className="recon-hello">
        <h2 className="recon-hello-title">
          Hello, @{operatorName}
          {/* Badge visible sur desktop uniquement */}
          <span className="recon-rank-desktop">
            <span className={`rank-badge rank-badge--${userRank.toLowerCase()}`}>
              {userRank === 'Injected' && (
                <>
                  <svg className="rank-badge__icon" width="7" height="8" viewBox="0 0 7 8" fill="currentColor" aria-hidden="true">
                    <path d="M3.5 0.5L6.5 4 3.5 7.5 0.5 4ZM3.5 2.5L2 4l1.5 1.5L5 4Z" fillRule="evenodd"/>
                  </svg>
                  <span className="rank-badge__aura" />
                </>
              )}
              {userRank === 'Breached' && (
                <>
                  <svg className="rank-badge__icon" width="6" height="9" viewBox="0 0 6 9" fill="currentColor" aria-hidden="true">
                    <path d="M4 0.5L1 4.5h1.8L2 8.5 6 4H4.2L4 0.5Z"/>
                  </svg>
                  <span className="rank-badge__bolt" />
                </>
              )}
              {userRank === 'Ghosted' && (
                <svg className="rank-badge__icon" width="8" height="9" viewBox="0 0 8 9" fill="currentColor" aria-hidden="true">
                  <path d="M4 0.5C1.8 0.5 0.5 2.1 0.5 4v4l1.1-0.8L2.7 8l1-0.8L4 7.5l0.3 0.7 1-0.8 1.1 0.8V4C7.5 2.1 6.2 0.5 4 0.5Z"/>
                </svg>
              )}
              <span className="rank-badge__label">{userRank}</span>
            </span>
          </span>
        </h2>
        <button
          type="button"
          onClick={openNewScanModal}
          disabled={loading || isGrabbing}
          className="recon-new-scan-btn"
          data-tour="scanner-start"
        >
          <svg className="recon-new-scan-icon" width="13" height="13" viewBox="0 0 13 13" fill="none">
            <circle cx="6.5" cy="6.5" r="5.5" stroke="currentColor" strokeWidth="1.2"/>
            <path d="M6.5 3.5v6M3.5 6.5h6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
          </svg>
          New Scan
        </button>
      </header>

      {(licenseLevel != null || currentLevelXp > 0) && (
        <div className="recon-xp-wrap">
          <div className="recon-xp-meta">
            <span className="recon-xp-badge">LVL {licenseLevel ?? 1}</span>
            <span className="recon-xp-progress">
              {nextLevelXp > 0 ? `${currentLevelXp.toLocaleString()} / ${nextLevelXp.toLocaleString()} XP` : 'MAX'}
            </span>
            {xpPct > 0 && <span className="recon-xp-pct">{xpPct}%</span>}
            {/* Badge visible sur mobile uniquement, aligné avec LVL/XP */}
            <span className="recon-rank-mobile">
              <span className={`rank-badge rank-badge--${userRank.toLowerCase()}`}>
                {userRank === 'Injected' && (
                  <>
                    <svg className="rank-badge__icon" width="7" height="8" viewBox="0 0 7 8" fill="currentColor" aria-hidden="true">
                      <path d="M3.5 0.5L6.5 4 3.5 7.5 0.5 4ZM3.5 2.5L2 4l1.5 1.5L5 4Z" fillRule="evenodd"/>
                    </svg>
                    <span className="rank-badge__aura" />
                  </>
                )}
                {userRank === 'Breached' && (
                  <>
                    <svg className="rank-badge__icon" width="6" height="9" viewBox="0 0 6 9" fill="currentColor" aria-hidden="true">
                      <path d="M4 0.5L1 4.5h1.8L2 8.5 6 4H4.2L4 0.5Z"/>
                    </svg>
                    <span className="rank-badge__bolt" />
                  </>
                )}
                {userRank === 'Ghosted' && (
                  <svg className="rank-badge__icon" width="8" height="9" viewBox="0 0 8 9" fill="currentColor" aria-hidden="true">
                    <path d="M4 0.5C1.8 0.5 0.5 2.1 0.5 4v4l1.1-0.8L2.7 8l1-0.8L4 7.5l0.3 0.7 1-0.8 1.1 0.8V4C7.5 2.1 6.2 0.5 4 0.5Z"/>
                  </svg>
                )}
                <span className="rank-badge__label">{userRank}</span>
              </span>
            </span>
          </div>
          <div className="recon-xp-track">
            <span className="recon-xp-fill" style={{ width: `${xpPct}%` }}>
              <span className="recon-xp-sheen" />
            </span>
          </div>
        </div>
      )}

        <AnimatePresence>
          {error && (
            <motion.div
              className="recon-error"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <span>{error}</span>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="recon-body">
          <div className="recon-main">
            <section className="recon-stats" aria-label="Scanner metrics">
              {/* REQ / Second — diagonal layout */}
              <article className="recon-stat recon-stat--diag">
                <p>REQ / S</p>
                <div className="recon-diag-row">
                  <div className="recon-diag-cell">
                    <span>REQ/S</span>
                    <strong>{scanStats || slotMetrics.some(m => m.scanStats) ? formatCount(combinedReqPerSec) : '—'}</strong>
                  </div>
                  <div className="recon-diag-divider" />
                  <div className="recon-diag-cell recon-diag-cell--right">
                    <span>URLs/S</span>
                    <strong>{scanStats || slotMetrics.some(m => m.scanStats) ? formatCount(combinedUrlsPerSec) : '—'}</strong>
                  </div>
                </div>
              </article>

              {/* Hits — inline badge tags */}
              <article className="recon-stat">
                <p>Hits</p>
                <strong>{scanStats || slotMetrics.some(m => m.scanStats) ? formatCount(combinedHitTotal) : '—'}</strong>
                <div className="recon-hit-tags">
                  <span className="recon-hit-tag recon-hit-tag--ok">{scanStats || slotMetrics.some(m => m.scanStats) ? formatCount(combinedValidN) : '—'} valid</span>
                  <span className="recon-hit-tag recon-hit-tag--bad">{scanStats || slotMetrics.some(m => m.scanStats) ? formatCount(combinedInvalidN) : '—'} inv.</span>
                  <span className="recon-hit-tag recon-hit-tag--special">{formatCount(dbSpecialN)} spec.</span>
                  <span className="recon-hit-tag recon-hit-tag--brut">{formatCount(dbBrutsN)} brut</span>
                </div>
              </article>

              {/* Active Scans */}
              <article className="recon-stat">
                <p>Active Scans</p>
                <strong className="recon-running-count">
                  {isScanActive || slotMetrics.some(m => m.status?.scanning) ? (
                    <span className="recon-running-num">
                      {(isScanActive ? 1 : 0) + slotMetrics.filter(m => m.status?.scanning).length}
                    </span>
                  ) : <span className="recon-running-num recon-running-num--idle">0</span>}
                  <span className="recon-running-label">running</span>
                </strong>
                <ul className="recon-split">
                  <li><span>All-time</span><b>{totalScans != null ? formatCount(totalScans) : '—'}</b></li>
                  <li><span>LvL</span><b>{licenseLevel != null ? formatCount(licenseLevel) : '—'}</b></li>
                </ul>
              </article>

              {/* Domains — with valid/invalid split bar */}
              <article className="recon-stat">
                <p>Domains</p>
                <strong>{isScanActive ? formatCount(domainsCount) : '0'}</strong>
                <div className="recon-mini">
                  <span className="recon-mini-track">
                    <span style={{ width: `${isScanActive && scanStats && scanStats.totalUrls > 0 ? scanProgress : 0}%` }} />
                  </span>
                  <em>{isScanActive && scanStats && scanStats.totalUrls > 0 ? `${scanProgress}%` : '—'}</em>
                </div>
                <div className="recon-dom-bar">
                  <div className="recon-dom-bar-track">
                    <span className="recon-dom-bar-valid" style={{ width: `${isScanActive ? validDomPct : 0}%` }} />
                    <span className="recon-dom-bar-invalid" style={{ width: `${isScanActive ? invalidDomPct : 0}%` }} />
                  </div>
                  <div className="recon-dom-bar-labels">
                    <span style={{ color: '#00E87A' }}>{isScanActive && validDomPct > 0 ? `${validDomPct}%` : '—'} valide</span>
                    <span style={{ color: '#FF3B55' }}>{isScanActive && invalidDomPct > 0 ? `${invalidDomPct}%` : '—'} inv.</span>
                  </div>
                </div>
              </article>

              {/* Proxies */}
              <article className="recon-stat">
                <p>Proxies</p>
                <strong>{proxiesKnown ? formatCount(proxiesCount) : '—'}</strong>
              </article>

              {/* Live — shows connection status + server count */}
              <article className="recon-stat recon-stat--live">
                <p>Live</p>
                <strong className="recon-live-badge">
                  <span className={`recon-live-dot${vpsIp ? ' recon-live-dot--on' : ''}`} />
                  {vpsIp ? 'LIVE' : '—'}
                </strong>
                <div className="recon-cpu-ram-row">
                  <div className="recon-cpu-ram-cell" style={{ flex: 1 }}>
                    <span>Servers</span>
                    <strong style={{ color: '#38bdf8' }}>{effectiveVpsList.length}</strong>
                  </div>
                </div>
              </article>
            </section>

            <section className="recon-card recon-board">
              <div className="recon-board-dist">
                <h3>Distribution</h3>
                <div className="recon-pie-row" ref={pieContainerRef} style={{ position: 'relative' }}>
                  <svg viewBox="0 0 100 100" className="recon-pie-svg" role="img" aria-label="Top services distribution"
                    onMouseLeave={() => setHoveredPieSlice(null)}>
                    <circle cx="50" cy="50" r={pieR} fill="none" stroke="#1a1a1f" strokeWidth="10" />
                    {pieTotal > 0 && pieSlices.map((slice) => {
                      const len = slice.visual * pieC;
                      const dash = `${len} ${pieC - len}`;
                      const offset = pieCursor;
                      pieCursor += len;
                      const isHovered = hoveredPieSlice?.key === slice.key;
                      return (
                        <circle
                          key={slice.key}
                          cx="50" cy="50" r={pieR}
                          fill="none"
                          stroke={slice.color}
                          strokeWidth={isHovered ? 13 : 10}
                          strokeOpacity={isHovered ? 1 : hoveredPieSlice ? 0.55 : 1}
                          strokeDasharray={dash}
                          strokeDashoffset={-offset}
                          transform="rotate(-90 50 50)"
                          style={{ cursor: 'pointer', transition: 'stroke-width 0.15s, stroke-opacity 0.15s' }}
                          onMouseEnter={(e) => {
                            if (!pieContainerRef.current) return;
                            const rect = pieContainerRef.current.getBoundingClientRect();
                            setHoveredPieSlice({
                              key: slice.key, label: slice.label,
                              value: slice.value,
                              pct: Math.round((slice.value / pieTotal) * 100),
                              color: slice.color,
                              x: e.clientX - rect.left,
                              y: e.clientY - rect.top,
                            });
                          }}
                        />
                      );
                    })}
                  </svg>
                  <ul className="recon-legend">
                    <li><i style={{ background: '#00E87A' }} />Valid <b>{allActiveStats.length > 0 ? formatCount(combinedValidN) : '—'}</b></li>
                    <li><i style={{ background: '#FF3B55' }} />Invalid <b>{allActiveStats.length > 0 ? formatCount(combinedInvalidN) : '—'}</b></li>
                    <li><i style={{ background: '#a78bfa' }} />Special <b>{formatCount(dbSpecialN)}</b></li>
                    <li><i style={{ background: '#f59e0b' }} />Bruts <b>{formatCount(dbBrutsN)}</b></li>
                  </ul>
                  {hoveredPieSlice && (
                    <div className="recon-pie-tooltip" style={{ left: hoveredPieSlice.x + 10, top: hoveredPieSlice.y - 10 }}>
                      <span className="recon-pie-tooltip-dot" style={{ background: hoveredPieSlice.color }} />
                      <span className="recon-pie-tooltip-label">{hoveredPieSlice.label}</span>
                      <span className="recon-pie-tooltip-val">{formatCount(hoveredPieSlice.value)}</span>
                      <span className="recon-pie-tooltip-pct">{hoveredPieSlice.pct}%</span>
                    </div>
                  )}
                </div>
                <h3>Top services</h3>
                {serviceRows.length === 0 ? (
                  <p className="recon-empty">No services</p>
                ) : (
                  <ul className="recon-services">
                    {serviceRows.map((row) => (
                      <li key={row.label}>
                        <span>{row.label}</span>
                        <span className="recon-svc-track">
                          <span style={{ width: `${Math.max(4, (row.value / serviceMax) * 100)}%`, background: serviceColor(row.label) }} />
                        </span>
                        <b>{formatCount(row.value)}</b>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="recon-board-flow">
                <div className="recon-line-top">
                  <h3>REQ / S</h3>
                  <strong>{allActiveStats.length > 0 ? formatCount(combinedReqPerSec) : '—'}</strong>
                </div>
                <div className="recon-line-plot">
                  {rangedPoints.length > 0 ? (
                    <>
                      <svg viewBox="0 0 100 100" preserveAspectRatio="none" role="img" aria-label="Débit req/s">
                        <defs>
                          <linearGradient id={flowGradId} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#22c55e" stopOpacity="0.25" />
                            <stop offset="100%" stopColor="#22c55e" stopOpacity="0" />
                          </linearGradient>
                        </defs>
                        {[25, 50, 75].map((y) => (
                          <line key={y} x1="0" x2="100" y1={y} y2={y} stroke="#ffffff" strokeDasharray="1.5 1.5" strokeWidth="0.3" strokeOpacity="0.06" />
                        ))}
                        {chartArea && <path d={chartArea} fill={`url(#${flowGradId})`} />}
                        {chartLine && (
                          <path d={chartLine} fill="none" stroke="#22c55e" strokeWidth="1.4" vectorEffect="non-scaling-stroke" />
                        )}
                      </svg>
                      <span className="recon-line-dot" style={{ left: `${chartDotX}%`, top: `${chartDotY}%` }} />
                    </>
                  ) : (
                    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="recon-fake-chart" role="img" aria-label="En attente de scan">
                      <defs>
                        <linearGradient id="fakeGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#22c55e" stopOpacity="0.1" />
                          <stop offset="100%" stopColor="#22c55e" stopOpacity="0" />
                        </linearGradient>
                        <clipPath id="fakeClip">
                          <rect x="0" y="0" width="100" height="100" />
                        </clipPath>
                      </defs>
                      {[25, 50, 75].map((y) => (
                        <line key={y} x1="0" x2="100" y1={y} y2={y} stroke="#ffffff" strokeDasharray="1.5 1.5" strokeWidth="0.3" strokeOpacity="0.04" />
                      ))}
                      <path
                        d="M0 72 C8 68 12 58 20 55 C28 52 32 62 40 58 C48 54 52 44 60 48 C68 52 72 38 80 42 C88 46 92 35 100 38 L100 100 L0 100 Z"
                        fill="url(#fakeGrad)"
                        clipPath="url(#fakeClip)"
                        className="recon-fake-area"
                      />
                      <path
                        d="M0 72 C8 68 12 58 20 55 C28 52 32 62 40 58 C48 54 52 44 60 48 C68 52 72 38 80 42 C88 46 92 35 100 38"
                        fill="none"
                        stroke="#22c55e"
                        strokeWidth="1"
                        strokeOpacity="0.3"
                        className="recon-fake-line"
                        vectorEffect="non-scaling-stroke"
                      />
                    </svg>
                  )}
                </div>
              </div>
            </section>

            <article className="recon-card recon-scan recon-server1">
              {/* Header */}
              <div className="recon-scan-head">
                <div className="recon-server-name-group">
                  <span className={`recon-live-pill${status ? ' recon-live-pill--on' : ''}`}>
                    <i className="recon-live-pill-dot" />
                    LIVE
                  </span>
                  <div className="recon-server-name-col">
                    <span className="recon-server1-title">SERVER 1</span>
                    <span className="recon-server-latency">{laneLatencyMs != null ? `${laneLatencyMs} ms` : '-- ms'}</span>
                  </div>
                </div>
                <span className={`recon-state${isScanActive ? ' recon-state--run' : ' recon-state--stopped'}`}>
                  {isScanActive ? 'RUNNING' : 'STOPPED'}
                </span>
                {isScanActive && (
                  <>
                    <span className="recon-scan-tag recon-scan-tag--list recon-scan-tag--active">
                      List{scanStats?.currentFile || selectedCheckFile ? ` : ${scanStats?.currentFile || selectedCheckFile}` : ''}
                    </span>
                    <span className="recon-scan-tag recon-scan-tag--part recon-scan-tag--active">
                      Parts{scanStats?.currentPart != null && scanStats.currentPart > 0 ? ` : ${scanStats.currentPart}${scanStats.totalParts && scanStats.totalParts > 1 ? ` / ${scanStats.totalParts}` : ''}` : ''}
                    </span>
                  </>
                )}
                {(scanStats?.totalScansLaunched != null && scanStats.totalScansLaunched > 0) && (
                  <span className="recon-server-scans-count">
                    {formatCount(scanStats.totalScansLaunched)} scans
                  </span>
                )}
                {isScanActive && (scanStats?.scanLabel || scanLabels[0]) && (
                  <span className="recon-scan-label-badge">{scanStats?.scanLabel || scanLabels[0]}</span>
                )}
                {isScanActive ? (
                  <button type="button" className="recon-act recon-act--stop" onClick={stopScan} disabled={loading || !status?.scanning}>
                    {loading ? '…' : 'Stop'}
                  </button>
                ) : (
                  <button type="button" className="recon-act recon-act--go" onClick={handleStartClick} disabled={loading || isGrabbing}>
                    {loading ? '…' : 'Start'}
                  </button>
                )}
              </div>

              {/* Metrics grid — 3-card mode */}
              <div className="recon-server1-grid recon-server1-grid--badge">
                {/* Card 1: REQ/S / URLs/S diagonal */}
                <div className="recon-server1-cell recon-server1-cell--badge recon-server1-cell--diag">
                  <span>SPEED</span>
                  <div className="recon-diag-row">
                    <div className="recon-diag-cell">
                      <span>REQ/S</span>
                      <strong>{scanStats ? formatCount(reqPerSec) : '—'}</strong>
                    </div>
                    <div className="recon-diag-divider" />
                    <div className="recon-diag-cell recon-diag-cell--right">
                      <span>URLs/S</span>
                      <strong>{scanStats ? formatCount(urlsPerSec) : '—'}</strong>
                    </div>
                  </div>
                </div>
                {/* Card 2: CPU / RAM diagonal */}
                <div className="recon-server1-cell recon-server1-cell--badge recon-server1-cell--diag">
                  <span>RESOURCES</span>
                  <div className="recon-diag-row">
                    <div className="recon-diag-cell">
                      <span>CPU</span>
                      <strong style={{ color: cpuColor }}>{scanStats ? `${cpuVal.toFixed(1)}%` : '—'}</strong>
                    </div>
                    <div className="recon-diag-divider" />
                    <div className="recon-diag-cell recon-diag-cell--right">
                      <span>RAM</span>
                      <strong style={{ color: ramColor }}>{scanStats ? `${ramVal.toFixed(1)}%` : '—'}</strong>
                    </div>
                  </div>
                </div>
                {/* Card 3: Hits + valid/invalid/special/brut */}
                <div className="recon-server1-cell recon-server1-cell--badge">
                  <span>HITS</span>
                  <strong>{scanStats ? formatCount(hitTotal) : '—'}</strong>
                  {(() => {
                    const total = validN + invalidN + dbSpecialN + dbBrutsN;
                    let valPct  = total > 0 ? (validN     / total) * 100 : 0;
                    let invPct  = total > 0 ? (invalidN   / total) * 100 : 0;
                    let supPct  = total > 0 ? (dbSpecialN / total) * 100 : 0;
                    let brutPct = total > 0 ? (dbBrutsN   / total) * 100 : 0;
                    // Ensure special segment is visible (min 4%) when present
                    const MIN_SUP = 4;
                    if (dbSpecialN > 0 && supPct < MIN_SUP) {
                      const boost = MIN_SUP - supPct;
                      if (brutPct >= boost) { brutPct -= boost; }
                      else if (invPct >= boost) { invPct -= boost; }
                      else if (valPct >= boost) { valPct -= boost; }
                      supPct = MIN_SUP;
                    }
                    return (
                      <div className="recon-server1-dom-row">
                        <span style={{ color: '#00E87A' }}>{scanStats ? formatCount(validN) : '0'} val.</span>
                        <span style={{ color: '#FF3B55' }}>{scanStats ? formatCount(invalidN) : '0'} inv.</span>
                        <span style={{ color: '#a78bfa' }}>{formatCount(dbSpecialN)} spec.</span>
                        <span style={{ color: '#f59e0b' }}>{formatCount(dbBrutsN)} brut</span>
                        <div className="recon-hits-ratio-bar">
                          <div className="recon-hits-ratio-fill recon-hits-ratio-fill--valid"   style={{ width: `${valPct}%` }} />
                          <div className="recon-hits-ratio-fill recon-hits-ratio-fill--invalid" style={{ width: `${invPct}%` }} />
                          <div className="recon-hits-ratio-fill recon-hits-ratio-fill--special" style={{ width: `${supPct}%` }} />
                          <div className="recon-hits-ratio-fill recon-hits-ratio-fill--brut"    style={{ width: `${brutPct}%` }} />
                        </div>
                      </div>
                    );
                  })()}
                </div>
                {/* Card 4: Duration & ETA */}
                <div className="recon-server1-cell recon-server1-cell--badge recon-server1-cell--diag">
                  <span>TIMING</span>
                  <div className="recon-diag-row">
                    <div className="recon-diag-cell">
                      <span>Duration</span>
                      <strong>{isScanActive && scanStats?.startTime ? formatDuration() : '—'}</strong>
                    </div>
                    <div className="recon-diag-divider" />
                    <div className="recon-diag-cell recon-diag-cell--right">
                      <span>ETA</span>
                      <strong>{isScanActive && scanStats?.eta ? scanStats.eta : '—'}</strong>
                    </div>
                  </div>
                </div>
              </div>

              {/* Progress bars — side by side */}
              <div className="recon-server1-progress">
                <div className="recon-progress-block">
                  <div className="recon-server1-bar-label">
                    <span>URLs</span>
                    <span className="recon-server1-bar-count">{isScanActive && scanStats ? `${formatCount(scanStats.processedUrls)} / ${formatCount(scanStats.totalUrls)}` : '— / —'}</span>
                  </div>
                  <div className="recon-meter recon-meter--progress recon-meter--split">
                    <span className="recon-meter-processed" style={{
                      width: `${isScanActive && scanStats && scanStats.totalUrls > 0 ? scanProgress : 0}%`,
                      background: scanProgress > 75 ? '#00E87A' : scanProgress > 40 ? '#F59E0B' : '#FF3B55'
                    }} />
                    <span className="recon-meter-remaining" style={{
                      width: `${isScanActive && scanStats && scanStats.totalUrls > 0 ? 100 - scanProgress : 0}%`
                    }} />
                  </div>
                  <div className="recon-server1-bar-pcts">
                    <span style={{ color: scanProgress > 75 ? '#00E87A' : scanProgress > 40 ? '#F59E0B' : '#FF3B55' }}>
                      {isScanActive && scanStats && scanStats.totalUrls > 0 ? `${scanProgress}% done` : '—'}
                    </span>
                    <span style={{ color: '#6b6b7a' }}>{isScanActive && scanStats && scanStats.totalUrls > 0 ? `${100 - scanProgress}% left` : '—'}</span>
                  </div>
                </div>
                <div className="recon-progress-block">
                  <div className="recon-server1-bar-label">
                    <span>Domains</span>
                  </div>
                  <div className="recon-meter recon-meter--progress recon-meter--split">
                    <span className="recon-meter-processed" style={{ width: `${validDomPct}%`, background: '#00E87A' }} />
                    <span className="recon-meter-remaining" style={{ width: `${invalidDomPct}%`, background: '#FF3B55', opacity: 0.75 }} />
                  </div>
                  <div className="recon-server1-bar-pcts">
                    <span style={{ color: '#00E87A' }}>{validDoms > 0 ? `${validDomPct}% valid` : '—'}</span>
                    <span style={{ color: '#FF3B55' }}>{invalidDoms > 0 ? `${invalidDomPct}% invalid` : '—'}</span>
                  </div>
                </div>
              </div>

            </article>

            {/* ── SERVER 2+ panels — always render at least 1 slot ── */}
            {Array.from({ length: Math.max(effectiveVpsList.length - 1, 1) }, (_, extraIdx) => {
              const vpsIdx = extraIdx + 1; // 1-based
              const m = slotMetrics[extraIdx] ?? { scanStats: null, status: null, loading: false, urlsPerSec: 0, reqPerSec: 0, hitTotal: 0, validN: 0, invalidN: 0, superN: 0, brutsN: 0, cpuVal: 0, ramVal: 0, cpuColor: '#00E87A', ramColor: '#00E87A', validDoms: 0, invalidDoms: 0, totalDoms: 0, validDomPct: 0, invalidDomPct: 0, scanProgress: 0 };
              const vps = effectiveVpsList[vpsIdx]; // undefined if not configured
              const isScanNActive = !!(m.status?.scanning);
              const serverLabel = `SERVER ${vpsIdx + 1}`;
              return (
              <article key={vpsIdx} className={`recon-card recon-scan recon-server1${!vps ? ' recon-server1--unconfigured' : ''}`}>
                <div className="recon-scan-head">
                  <div className="recon-server-name-group">
                    <span className={`recon-live-pill${vps && m.status ? ' recon-live-pill--on' : ''}`}>
                      <i className="recon-live-pill-dot" />
                      LIVE
                    </span>
                    <div className="recon-server-name-col">
                      <span className="recon-server1-title" style={{ color: '#38bdf8' }}>{serverLabel}</span>
                      <span className="recon-server-latency">{vps && (extraSlots[extraIdx]?.latencyMs != null) ? `${extraSlots[extraIdx]!.latencyMs} ms` : '-- ms'}</span>
                    </div>
                  </div>
                  {!vps ? (
                    <span className="recon-state recon-state--unconfigured">NOT CONFIGURED</span>
                  ) : (
                    <span className={`recon-state${isScanNActive ? ' recon-state--run' : ' recon-state--stopped'}`}>
                      {isScanNActive ? 'RUNNING' : 'STOPPED'}
                    </span>
                  )}
                  {vps && (
                    <>
                      {isScanNActive && (
                        <>
                          <span className="recon-scan-tag recon-scan-tag--list recon-scan-tag--active">
                            List{m.scanStats?.currentFile ? ` : ${m.scanStats.currentFile}` : ''}
                          </span>
                          <span className="recon-scan-tag recon-scan-tag--part recon-scan-tag--active">
                            Parts{m.scanStats?.currentPart != null && m.scanStats.currentPart > 0 ? ` : ${m.scanStats.currentPart}${m.scanStats.totalParts && m.scanStats.totalParts > 1 ? ` / ${m.scanStats.totalParts}` : ''}` : ''}
                          </span>
                        </>
                      )}
                      {(m.scanStats?.totalScansLaunched != null && m.scanStats.totalScansLaunched > 0) && (
                        <span className="recon-server-scans-count">
                          {formatCount(m.scanStats.totalScansLaunched)} scans
                        </span>
                      )}
                      {isScanNActive && (m.scanStats?.scanLabel || scanLabels[vpsIdx]) && (
                        <span className="recon-scan-label-badge">{m.scanStats?.scanLabel || scanLabels[vpsIdx]}</span>
                      )}
                    </>
                  )}
                  {!vps ? (
                    <button type="button" className="recon-act recon-act--add" onClick={() => window.open('https://glitchcloud.app', '_blank')}>
                      + Add Server
                    </button>
                  ) : isScanNActive ? (
                    <button type="button" className="recon-act recon-act--stop" onClick={() => stopExtraScan(vpsIdx)} disabled={m.loading}>
                      {m.loading ? '…' : 'Stop'}
                    </button>
                  ) : (
                    <button type="button" className="recon-act recon-act--go" onClick={handleStartClickN(vpsIdx)} disabled={m.loading || isGrabbing}>
                      {m.loading ? '…' : 'Start'}
                    </button>
                  )}
                </div>

                <div className="recon-server1-grid recon-server1-grid--badge">
                  <div className="recon-server1-cell recon-server1-cell--badge recon-server1-cell--diag">
                    <span>SPEED</span>
                    <div className="recon-diag-row">
                      <div className="recon-diag-cell">
                        <span>REQ/S</span>
                        <strong>{vps && m.scanStats ? formatCount(m.reqPerSec) : '—'}</strong>
                      </div>
                      <div className="recon-diag-divider" />
                      <div className="recon-diag-cell recon-diag-cell--right">
                        <span>URLs/S</span>
                        <strong>{vps && m.scanStats ? formatCount(m.urlsPerSec) : '—'}</strong>
                      </div>
                    </div>
                  </div>
                  <div className="recon-server1-cell recon-server1-cell--badge recon-server1-cell--diag">
                    <span>RESOURCES</span>
                    <div className="recon-diag-row">
                      <div className="recon-diag-cell">
                        <span>CPU</span>
                        <strong style={{ color: m.cpuColor }}>{vps && m.scanStats ? `${m.cpuVal.toFixed(1)}%` : '—'}</strong>
                      </div>
                      <div className="recon-diag-divider" />
                      <div className="recon-diag-cell recon-diag-cell--right">
                        <span>RAM</span>
                        <strong style={{ color: m.ramColor }}>{vps && m.scanStats ? `${m.ramVal.toFixed(1)}%` : '—'}</strong>
                      </div>
                    </div>
                  </div>
                  <div className="recon-server1-cell recon-server1-cell--badge">
                    <span>HITS</span>
                    <strong>{vps && m.scanStats ? formatCount(m.hitTotal) : '—'}</strong>
                    {(() => {
                      const tot = m.validN + m.invalidN + m.superN + m.brutsN;
                      let vp = tot > 0 ? (m.validN   / tot) * 100 : 0;
                      let ip = tot > 0 ? (m.invalidN / tot) * 100 : 0;
                      let sp = tot > 0 ? (m.superN   / tot) * 100 : 0;
                      let bp = tot > 0 ? (m.brutsN   / tot) * 100 : 0;
                      const MIN_SP = 4;
                      if (m.superN > 0 && sp < MIN_SP) {
                        const boost = MIN_SP - sp;
                        if (bp >= boost) { bp -= boost; }
                        else if (ip >= boost) { ip -= boost; }
                        else if (vp >= boost) { vp -= boost; }
                        sp = MIN_SP;
                      }
                      return (
                        <div className="recon-server1-dom-row">
                          <span style={{ color: '#00E87A' }}>{vps && m.scanStats ? formatCount(m.validN) : '0'} val.</span>
                          <span style={{ color: '#FF3B55' }}>{vps && m.scanStats ? formatCount(m.invalidN) : '0'} inv.</span>
                          <span style={{ color: '#a78bfa' }}>{vps && m.scanStats ? formatCount(m.superN) : '0'} spec.</span>
                          <span style={{ color: '#f59e0b' }}>{vps && m.scanStats ? formatCount(m.brutsN) : '0'} brut</span>
                          <div className="recon-hits-ratio-bar">
                            <div className="recon-hits-ratio-fill recon-hits-ratio-fill--valid"   style={{ width: `${vp}%` }} />
                            <div className="recon-hits-ratio-fill recon-hits-ratio-fill--invalid" style={{ width: `${ip}%` }} />
                            <div className="recon-hits-ratio-fill recon-hits-ratio-fill--special" style={{ width: `${sp}%` }} />
                            <div className="recon-hits-ratio-fill recon-hits-ratio-fill--brut"    style={{ width: `${bp}%` }} />
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  <div className="recon-server1-cell recon-server1-cell--badge recon-server1-cell--diag">
                    <span>TIMING</span>
                    <div className="recon-diag-row">
                      <div className="recon-diag-cell">
                        <span>Duration</span>
                        <strong>{isScanNActive && m.scanStats?.startTime ? (() => { try { const d = Date.now() - new Date(m.scanStats.startTime).getTime(); const h = Math.floor(d/3600000); const min = Math.floor((d%3600000)/60000); const s = Math.floor((d%60000)/1000); return `${h}h ${min}m ${s}s`; } catch { return '—'; } })() : '—'}</strong>
                      </div>
                      <div className="recon-diag-divider" />
                      <div className="recon-diag-cell recon-diag-cell--right">
                        <span>ETA</span>
                        <strong>{isScanNActive && m.scanStats?.eta ? m.scanStats.eta : '—'}</strong>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="recon-server1-progress">
                  <div className="recon-progress-block">
                    <div className="recon-server1-bar-label">
                      <span>URLs</span>
                      <span className="recon-server1-bar-count">{isScanNActive && m.scanStats ? `${formatCount(m.scanStats.processedUrls)} / ${formatCount(m.scanStats.totalUrls)}` : '— / —'}</span>
                    </div>
                    <div className="recon-meter recon-meter--progress recon-meter--split">
                      <span className="recon-meter-processed" style={{
                        width: `${isScanNActive && m.scanStats && m.scanStats.totalUrls > 0 ? m.scanProgress : 0}%`,
                        background: m.scanProgress > 75 ? '#00E87A' : m.scanProgress > 40 ? '#F59E0B' : '#FF3B55'
                      }} />
                      <span className="recon-meter-remaining" style={{
                        width: `${isScanNActive && m.scanStats && m.scanStats.totalUrls > 0 ? 100 - m.scanProgress : 0}%`
                      }} />
                    </div>
                    <div className="recon-server1-bar-pcts">
                      <span style={{ color: m.scanProgress > 75 ? '#00E87A' : m.scanProgress > 40 ? '#F59E0B' : '#FF3B55' }}>
                        {isScanNActive && m.scanStats && m.scanStats.totalUrls > 0 ? `${m.scanProgress}% done` : '—'}
                      </span>
                      <span style={{ color: '#6b6b7a' }}>{isScanNActive && m.scanStats && m.scanStats.totalUrls > 0 ? `${100 - m.scanProgress}% left` : '—'}</span>
                    </div>
                  </div>
                  <div className="recon-progress-block">
                    <div className="recon-server1-bar-label">
                      <span>Domains</span>
                    </div>
                    <div className="recon-meter recon-meter--progress recon-meter--split">
                      <span className="recon-meter-processed" style={{ width: `${vps ? m.validDomPct : 0}%`, background: '#00E87A' }} />
                      <span className="recon-meter-remaining" style={{ width: `${vps ? m.invalidDomPct : 0}%`, background: '#FF3B55', opacity: 0.75 }} />
                    </div>
                    <div className="recon-server1-bar-pcts">
                      <span style={{ color: '#00E87A' }}>{vps && m.validDoms > 0 ? `${m.validDomPct}% valid` : '—'}</span>
                      <span style={{ color: '#FF3B55' }}>{vps && m.invalidDoms > 0 ? `${m.invalidDomPct}% invalid` : '—'}</span>
                    </div>
                  </div>
                </div>

              </article>
              );
            })}

          </div>

          <aside className="recon-rail">
            <section className="recon-hits-panel">
              {(() => {
                const sortedHits = [...recentHits].sort((a, b) => new Date(b.discoveredAt).getTime() - new Date(a.discoveredAt).getTime());
                const totalPages = Math.max(1, Math.ceil(sortedHits.length / HITS_PER_PAGE));
                const safePage = Math.min(hitsPage, totalPages - 1);
                const pageHits = sortedHits.slice(safePage * HITS_PER_PAGE, (safePage + 1) * HITS_PER_PAGE);
                return (
                  <>
                    <div className="recon-hits-header">
                      <h3>Recent Hits</h3>
                      {sortedHits.length > HITS_PER_PAGE && (
                        <div className="recon-hits-pager">
                          <button
                            className="recon-hits-pager-btn"
                            disabled={safePage === 0}
                            onClick={() => setHitsPage(p => Math.max(0, p - 1))}
                            aria-label="Page précédente"
                          >‹</button>
                          <span className="recon-hits-pager-label">{safePage + 1}/{totalPages}</span>
                          <button
                            className="recon-hits-pager-btn"
                            disabled={safePage >= totalPages - 1}
                            onClick={() => setHitsPage(p => Math.min(totalPages - 1, p + 1))}
                            aria-label="Page suivante"
                          >›</button>
                        </div>
                      )}
                    </div>
                    {sortedHits.length === 0 ? (
                      <p className="recon-empty">No hits</p>
                    ) : (
                      <ul className="recon-hits">
                        {pageHits.map((hit) => {
                          const svc = hit.services?.[0] || '';
                          const svcColor = serviceColor(svc);
                          const apiType = inferApiType(hit.url || '');
                          const apiSnippet = extractApiSnippet(hit.credentials, svc);
                          let hostname = '';
                          try { hostname = new URL(hit.url || '').hostname; } catch { hostname = hit.url || ''; }
                          if (hostname.length > 28) hostname = hostname.slice(0, 27) + '…';
                          const xp = calcHitXP(hit);
                          const tier = hitXpTier(xp);
                          const xpLabel = tier === 'legendary' ? `⚡ +${xp}` : `+${xp}`;
                          return (
                            <li
                              key={hit.id}
                              className="recon-hit-item recon-hit-item--clickable"
                              onClick={() => {
                                localStorage.setItem('pendingHitId', hit.id);
                                setActiveTab?.('myhits');
                              }}
                            >
                              <div className="recon-hit-body">
                                <div className="recon-hit-top-row">
                                  {hit.status === 'valid' && (
                                    <span className="recon-hit-valid-badge">✓ Valid</span>
                                  )}
                                  {svc && (
                                    <span className="recon-hit-svc-badge" style={{ color: svcColor, borderColor: `${svcColor}44`, background: `${svcColor}12` }}>
                                      {svc}
                                    </span>
                                  )}
                                  {apiType && (
                                    <span className="recon-api-badge" style={{ color: apiType.color, borderColor: `${apiType.color}44`, background: `${apiType.color}11` }}>
                                      {apiType.label}
                                    </span>
                                  )}
                                  <time className="recon-hit-time">{formatHitAge(hit.discoveredAt)}</time>
                                </div>
                                <div className="recon-hit-bottom-row">
                                  {apiSnippet && (
                                    <span className="recon-hit-api-snippet" title={hit.url}>{apiSnippet}</span>
                                  )}
                                  {hostname && (
                                    <span className="recon-hit-url" title={hit.url}>{hostname}</span>
                                  )}
                                </div>
                              </div>
                              <span className={`recon-hit-xp recon-hit-xp--${tier}`}>{xpLabel}</span>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </>
                );
              })()}
            </section>
            {(() => {
              const exploits = (scanStats?.topExploits ?? []).filter(e => e.hits > 0).slice(0, 6);
              if (!exploits.length) return null;
              const exploitMax = exploits[0]?.hits || 1;
              return (
                <section className="recon-exploits-panel">
                  <h3>Top Exploits</h3>
                  <ul className="recon-exploits">
                    {exploits.map((e, idx) => {
                      const pct = Math.max(3, Math.round((e.hits / exploitMax) * 100));
                      const isTop = idx === 0;
                      return (
                        <li key={e.name} className={`recon-exploit-item${isTop ? ' recon-exploit-item--top' : ''}`}>
                          <span className="recon-exploit-rank">#{idx + 1}</span>
                          <span className="recon-exploit-name">{e.name}</span>
                          <span className="recon-exploit-bar-wrap">
                            <span className="recon-exploit-bar-fill" style={{ width: `${pct}%` }} />
                          </span>
                          <b className="recon-exploit-hits">{formatCount(e.hits)}</b>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              );
            })()}
            <section className="recon-metrics-panel">
              <h3>Metrics</h3>
              <dl className="recon-metrics">
                <div><dt>Rate</dt><dd>{hitRateLabel}</dd></div>
                <div><dt>Remaining</dt><dd>{remainingUrls != null ? formatCount(remainingUrls) : '—'}</dd></div>
                <div><dt>Proxies</dt><dd>{proxiesKnown ? formatCount(proxiesCount) : '—'}</dd></div>
                <div><dt>Special</dt><dd>{scanStats ? formatCount(superN) : '—'}</dd></div>
                <div><dt>req/s</dt><dd>{scanStats ? formatCount(reqPerSec) : '—'}</dd></div>
                {laneLatencyMs != null && (
                  <div><dt>RTT</dt><dd>{laneLatencyMs} ms</dd></div>
                )}
              </dl>
              {/* ── AVG CPU half-circle gauge ── */}
              {(() => {
                const R = 38;
                const cx = 60, cy = 50;
                const halfCirc = Math.PI * R;
                const fullCirc = 2 * Math.PI * R;
                const filled = (avgCpu / 100) * halfCirc;
                /* correct half-circle needle: 0% → left, 50% → top, 100% → right */
                const needleRad = Math.PI * (1 - avgCpu / 100);
                const needleX2 = cx + R * Math.cos(needleRad);
                const needleY2 = cy - R * Math.sin(needleRad);
                const gaugeId = flowGradId + '_cpu';
                return (
                  <div className="recon-cpu-gauge-wrap">
                    <svg viewBox="0 0 120 78" className="recon-cpu-gauge-svg" aria-label={`AVG CPU ${avgCpu}%`}>
                      <defs>
                        <linearGradient id={gaugeId} x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#3b82f6" />
                          <stop offset="33%" stopColor="#00E87A" />
                          <stop offset="66%" stopColor="#F59E0B" />
                          <stop offset="100%" stopColor="#FF3B55" />
                        </linearGradient>
                        <filter id={`${gaugeId}_glow`}>
                          <feGaussianBlur stdDeviation="2.5" result="blur" />
                          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                        </filter>
                      </defs>
                      {/* Background track */}
                      <circle cx={cx} cy={cy} r={R} fill="none" stroke="#1e1e26" strokeWidth="7"
                        strokeDasharray={`${halfCirc} ${fullCirc}`}
                        strokeLinecap="round"
                        transform={`rotate(-180 ${cx} ${cy})`} />
                      {/* Colored fill */}
                      <circle cx={cx} cy={cy} r={R} fill="none" stroke={`url(#${gaugeId})`} strokeWidth="7"
                        strokeDasharray={`${filled} ${fullCirc}`}
                        strokeLinecap="round"
                        transform={`rotate(-180 ${cx} ${cy})`}
                        style={{ transition: 'stroke-dasharray 0.6s cubic-bezier(0.4,0,0.2,1)' }} />
                      {/* Needle */}
                      <g filter={avgCpu > 75 ? `url(#${gaugeId}_glow)` : undefined}>
                        <line x1={cx} y1={cy} x2={needleX2.toFixed(2)} y2={needleY2.toFixed(2)}
                          stroke={avgCpuColor} strokeWidth="2" strokeLinecap="round"
                          style={{ transition: 'all 0.6s cubic-bezier(0.4,0,0.2,1)' }} />
                        <circle cx={cx} cy={cy} r="3.5" fill={avgCpuColor} />
                      </g>
                      {/* Center value — sits below pivot inside arc */}
                      <text x={cx} y={cy + 14} textAnchor="middle" className="recon-gauge-label-val" style={{ fill: avgCpuColor }}>{avgCpu}%</text>
                      {/* Tick labels — just outside arc ends */}
                      <text x="13" y={cy + 3} textAnchor="middle" className="recon-gauge-label-tick">0</text>
                      <text x="107" y={cy + 3} textAnchor="middle" className="recon-gauge-label-tick">100</text>
                      {/* Title below arc */}
                      <text x={cx} y={cy + 26} textAnchor="middle" className="recon-gauge-label-sub">AVG CPU</text>
                    </svg>
                  </div>
                );
              })()}
            </section>
          </aside>
        </div>
      </div>

      {/* ── File selection modal — Step 1 / 2 ── */}
      <AnimatePresence>
        {showFileModal && (
          <motion.div className="lm-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowFileModal(false)}>
            <motion.div className="lm-panel" initial={{ scale: 0.96, y: 16, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }} exit={{ scale: 0.96, y: 16, opacity: 0 }} transition={{ type: 'spring', stiffness: 320, damping: 28 }} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
              <div className="lm-header">
                <div className="lm-header-left">
                  <span className="lm-step">1 / 2</span>
                  <div>
                    <p className="lm-label">Domain list</p>
                    <h2 className="lm-title">Select file</h2>
                  </div>
                </div>
                <div className="lm-header-right">
                  <button type="button" className="lm-close" onClick={() => setShowFileModal(false)}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                  </button>
                </div>
              </div>

              <div className="lm-body">
                <div className="lm-sync-row">
                  <span className="lm-hint">{selectedCheckFile ? `✓ ${selectedCheckFile}` : 'No file selected'}</span>
                  <button type="button" className="lm-sync" onClick={loadCheckFiles} disabled={loadingCheckFiles}>
                    {loadingCheckFiles ? '…' : '↺ Sync'}
                  </button>
                </div>

                {loadingCheckFiles ? (
                  <div className="lm-empty"><span className="lm-spin" /><p>Loading…</p></div>
                ) : checkFiles.length === 0 ? (
                  <div className="lm-empty">
                    <p>No files in Check folder</p>
                    <p className="lm-hint">Import domains from Domains tab</p>
                  </div>
                ) : (
                  <div className="lm-file-list">
                    {checkFiles.map((file) => {
                      const sel = selectedCheckFile === file.name;
                      return (
                        <button key={file.name} type="button" className={`lm-file${sel ? ' lm-file--on' : ''}`} onClick={() => selectCheckFile(file.name)}>
                          <span className={`lm-radio${sel ? ' lm-radio--on' : ''}`}>{sel && <span className="lm-radio-dot" />}</span>
                          <span className="lm-file-name">{file.name}</span>
                          <span className="lm-file-meta">{formatCheckFileSize(file.size)}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="lm-footer">
                <button type="button" className="lm-btn lm-btn--ghost" onClick={() => setShowFileModal(false)}>Cancel</button>
                <button type="button" className="lm-btn lm-btn--next" onClick={handleContinueToConfig} disabled={!selectedCheckFile || loadingCheckFiles || checkFiles.length === 0}>Services →</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Services / Targets modal — Step 2 / 2 ── */}
      <AnimatePresence>
        {showRegexModal && (() => {
          const SVC_ICONS: Record<string, JSX.Element> = {
            AWS:        <svg viewBox="0 0 24 24" fill="none"><path d="M6.8 10.6c0 .4.1.7.2 1 .1.3.3.5.6.7.3.2.6.3 1 .4v.6h-.6v.3h.6v.5c-.8.1-1.4.3-1.8.7-.4.4-.6.8-.6 1.4 0 .5.2.9.5 1.2.3.3.8.5 1.3.5.4 0 .7-.1 1-.2.3-.1.5-.3.6-.5h.1c0 .2.1.5.3.6h1.3v-.3c-.2 0-.4-.1-.4-.2-.1-.1-.1-.3-.1-.6v-1.6c0-.6-.2-1-.6-1.3-.4-.3-.9-.4-1.5-.4v-.5h.6v-.3h-.6v-.6c.4-.1.7-.2.9-.4.2-.2.3-.5.3-.9 0-.3-.1-.6-.3-.8-.2-.2-.4-.3-.7-.3-.3 0-.5.1-.7.3-.2.1-.3.4-.4.7zm1.3 4.1c.2 0 .4.1.5.2.1.1.2.3.2.5v.9c0 .2-.1.4-.2.5-.1.1-.3.2-.5.2-.2 0-.3-.1-.5-.2-.1-.1-.2-.3-.2-.5 0-.3.1-.5.3-.7.2-.2.3-.3.4-.3v-.6z" fill="#FF9900"/><path d="M17.5 14.8c-.3.2-.5.3-.6.3-.1 0-.2-.1-.2-.3 0-.3.3-.6.8-.8.3-.1.6-.2.8-.2v.4c-.2.2-.5.4-.8.6zm2.5.6v-2.9c0-.6-.2-1-.5-1.3-.3-.3-.8-.4-1.5-.4-.6 0-1.1.1-1.5.4-.4.2-.6.6-.6 1h1.2c.1-.4.4-.6.9-.6.3 0 .5.1.6.2.1.1.2.3.2.5v.3c-.3 0-.7.1-1 .2-.5.1-.9.3-1.2.6-.3.3-.4.6-.4 1 0 .4.1.7.4.9.3.2.6.4 1 .4.4 0 .7-.1 1-.3.1-.1.2-.2.3-.3 0 .2.1.3.1.5h1.2c-.1-.3-.2-.7-.2-1.2z" fill="#FF9900"/><path d="M3 17.5c2.2 1.5 4.9 2.3 7.8 2.1 2.1-.1 4.2-.7 6-1.8l-1-.8c-1.5.9-3.2 1.4-5 1.5-2.5.1-4.8-.6-6.8-1.9l-1 .9z" fill="#FF9900"/></svg>,
            SendGrid:   <svg viewBox="0 0 24 24" fill="none"><rect width="24" height="24" rx="4" fill="#1A82E2"/><path d="M4 12h7v7H4z" fill="white" opacity=".6"/><path d="M11 5h7v7H11z" fill="white"/><path d="M4 5h7v7H4z" fill="white" opacity=".3"/><path d="M11 12h7v7H11z" fill="white" opacity=".3"/></svg>,
            Brevo:      <svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" fill="#0092FF"/><text x="12" y="16" textAnchor="middle" fontSize="10" fontWeight="700" fill="white">b</text></svg>,
            MailJet:    <svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" fill="#E84393"/><path d="M6 8l6 4 6-4v8H6V8z" fill="white" opacity=".9"/><path d="M6 8l6 4 6-4" stroke="white" strokeWidth="1.5" strokeLinejoin="round"/></svg>,
            MailGun:    <svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" fill="#F06B66"/><path d="M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zm0 2c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 1.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5z" fill="white"/></svg>,
            Tencent:    <svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" fill="#07C160"/><text x="12" y="16" textAnchor="middle" fontSize="11" fontWeight="800" fill="white">T</text></svg>,
            Stripe:     <svg viewBox="0 0 24 24" fill="none"><rect width="24" height="24" rx="6" fill="#635BFF"/><path d="M11.5 9.3c0-.6.5-.9 1.3-.9 1.1 0 2.1.3 3 .8v-2.8c-1-.4-2-.6-3-.6-2.4 0-4.1 1.3-4.1 3.4 0 3.3 4.5 2.8 4.5 4.3 0 .7-.6 1-1.5 1-1.2 0-2.5-.5-3.5-1.2v2.9c1.1.5 2.3.7 3.5.7 2.5 0 4.3-1.2 4.3-3.4-.1-3.5-4.5-2.9-4.5-4.2z" fill="white"/></svg>,
            SMTP:       <svg viewBox="0 0 24 24" fill="none"><rect x="3" y="6" width="18" height="13" rx="2" stroke="#94a3b8" strokeWidth="1.5" fill="none"/><path d="M3 8l9 6 9-6" stroke="#94a3b8" strokeWidth="1.5" strokeLinejoin="round"/></svg>,
            PostMark:   <svg viewBox="0 0 24 24" fill="none"><rect width="24" height="24" rx="4" fill="#FFDE00"/><text x="12" y="16" textAnchor="middle" fontSize="9" fontWeight="800" fill="#1a1a1a">PM</text></svg>,
            Twilio:     <svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" fill="#F22F46"/><circle cx="9" cy="9" r="2" fill="white"/><circle cx="15" cy="9" r="2" fill="white"/><circle cx="9" cy="15" r="2" fill="white"/><circle cx="15" cy="15" r="2" fill="white"/></svg>,
            Sparkpost:  <svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" fill="#FA6423"/><path d="M12 5l1.6 4.8H19l-4.1 3 1.6 4.8L12 14.6l-4.5 3 1.6-4.8L5 9.8h5.4z" fill="white"/></svg>,
            Aliyun:     <svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" fill="#FF6A00"/><path d="M7 13.5c0-1.5.6-2.8 1.6-3.8l-1.1-1.1C6.2 9.9 5.5 11.6 5.5 13.5h1.5zm9.5 0c0 1.9-.7 3.6-1.9 4.9l1.1 1.1c1.4-1.5 2.3-3.5 2.3-5.7h-1.5zm-9.1 1.5h1.5c.2.9.9 1.6 1.9 1.6v1.5c-1.9 0-3.4-1.4-3.4-3.1zm6.9-3.1c-1 0-1.7-.7-1.9-1.6H11a3.1 3.1 0 003.4 3.1V12a1.6 1.6 0 01-1.1-.6z" fill="white"/></svg>,
            OpenAI:     <svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" fill="#000"/><path d="M12 5.5c3.6 0 6.5 2.9 6.5 6.5S15.6 18.5 12 18.5 5.5 15.6 5.5 12 8.4 5.5 12 5.5zm0 2c-2.5 0-4.5 2-4.5 4.5s2 4.5 4.5 4.5 4.5-2 4.5-4.5-2-4.5-4.5-4.5zm0 1.5c1.7 0 3 1.3 3 3s-1.3 3-3 3-3-1.3-3-3 1.3-3 3-3z" fill="white"/></svg>,
            Anthropic:  <svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" fill="#D97757"/><text x="12" y="16" textAnchor="middle" fontSize="10" fontWeight="800" fill="white">A</text></svg>,
            'Google AI': <svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" fill="#fff"/><text x="6.5" y="15" fontSize="10" fontWeight="800" fill="#4285F4">G</text><circle cx="16.5" cy="10" r="3" fill="#34A853"/><path d="M14 10h5" stroke="#FBBC05" strokeWidth="1.5"/></svg>,
            HuggingFace:<svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" fill="#FFD21E"/><circle cx="9.5" cy="11" r="1.2" fill="#333"/><circle cx="14.5" cy="11" r="1.2" fill="#333"/><path d="M9 15.5c.8.8 2 1.3 3 1.3s2.2-.5 3-1.3" stroke="#333" strokeWidth="1.2" strokeLinecap="round"/></svg>,
            Groq:        <svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" fill="#F55036"/><text x="12" y="16" textAnchor="middle" fontSize="10" fontWeight="800" fill="white">G</text></svg>,
            Cohere:     <svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" fill="#D4A843"/><circle cx="12" cy="12" r="4" fill="white"/><circle cx="8" cy="9" r="2" fill="white"/></svg>,
            Mistral:    <svg viewBox="0 0 24 24" fill="none"><rect width="24" height="24" rx="4" fill="#FF7000"/><rect x="5" y="5" width="4" height="4" fill="#fff"/><rect x="10" y="5" width="4" height="4" fill="#fff"/><rect x="15" y="5" width="4" height="4" fill="#fff"/><rect x="5" y="10" width="4" height="4" fill="#fff"/><rect x="10" y="10" width="4" height="4" fill="rgba(255,255,255,.4)"/><rect x="5" y="15" width="4" height="4" fill="#fff"/></svg>,
            'Azure AI': <svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" fill="#0078D4"/><path d="M8 16l4-9 4 9H8z" fill="white" opacity=".8"/><path d="M10 12.5h4" stroke="white" strokeWidth="1.5"/></svg>,
            'Vertex AI': <svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" fill="#4285F4"/><path d="M7 17l5-10 5 10H7z" fill="white" opacity=".9"/></svg>,
            OpenRouter: <svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" fill="#111"/><circle cx="12" cy="12" r="4" stroke="white" strokeWidth="1.5"/><path d="M12 2v4M12 18v4M2 12h4M18 12h4" stroke="white" strokeWidth="1.2" opacity=".5"/></svg>,
            OpenCode:   <svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" fill="#18181b"/><path d="M9 9l-3 3 3 3M15 9l3 3-3 3" stroke="#00E87A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,
            MiniMax:    <svg viewBox="0 0 24 24" fill="none"><rect width="24" height="24" rx="6" fill="#6366F1"/><text x="12" y="16" textAnchor="middle" fontSize="8" fontWeight="800" fill="white">MM</text></svg>,
          };
          const emailServices = [
            { label: 'AWS',       on: enableRegexAWS,       toggle: () => setEnableRegexAWS(v => !v) },
            { label: 'SendGrid',  on: enableRegexSendGrid,  toggle: () => setEnableRegexSendGrid(v => !v) },
            { label: 'Brevo',     on: enableRegexBrevo,     toggle: () => setEnableRegexBrevo(v => !v) },
            { label: 'MailJet',   on: enableRegexMailJet,   toggle: () => setEnableRegexMailJet(v => !v) },
            { label: 'MailGun',   on: enableRegexMailGun,   toggle: () => setEnableRegexMailGun(v => !v) },
            { label: 'Tencent',   on: enableRegexTencent,   toggle: () => setEnableRegexTencent(v => !v) },
            { label: 'Stripe',    on: enableRegexStripe,    toggle: () => setEnableRegexStripe(v => !v) },
            { label: 'SMTP',      on: enableRegexSMTP,      toggle: () => setEnableRegexSMTP(v => !v) },
            { label: 'PostMark',  on: enableRegexPostMark,  toggle: () => setEnableRegexPostMark(v => !v) },
            { label: 'Twilio',    on: enableRegexTwilio,    toggle: () => setEnableRegexTwilio(v => !v) },
            { label: 'Sparkpost', on: enableRegexSparkpost, toggle: () => setEnableRegexSparkpost(v => !v) },
            { label: 'Aliyun',    on: enableRegexAliyun,    toggle: () => setEnableRegexAliyun(v => !v) },
          ];
          const aiServices = [
            { label: 'OpenAI',      on: enableRegexOpenAI,      toggle: () => setEnableRegexOpenAI(v => !v) },
            { label: 'Anthropic',   on: enableRegexAnthropic,   toggle: () => setEnableRegexAnthropic(v => !v) },
            { label: 'Google AI',   on: enableRegexGoogleAI,    toggle: () => setEnableRegexGoogleAI(v => !v) },
            { label: 'HuggingFace', on: enableRegexHuggingFace, toggle: () => setEnableRegexHuggingFace(v => !v) },
            { label: 'Groq',        on: enableRegexGroq,        toggle: () => setEnableRegexGroq(v => !v) },
            { label: 'Cohere',      on: enableRegexCohere,      toggle: () => setEnableRegexCohere(v => !v) },
            { label: 'Mistral',     on: enableRegexMistral,     toggle: () => setEnableRegexMistral(v => !v) },
            { label: 'Azure AI',    on: enableRegexAzureOpenAI, toggle: () => setEnableRegexAzureOpenAI(v => !v) },
            { label: 'Vertex AI',   on: enableRegexVertexAI,    toggle: () => setEnableRegexVertexAI(v => !v) },
            { label: 'OpenRouter',  on: enableRegexOpenRouter,  toggle: () => setEnableRegexOpenRouter(v => !v) },
            { label: 'OpenCode',    on: enableRegexOpenCode,    toggle: () => setEnableRegexOpenCode(v => !v) },
            { label: 'MiniMax',     on: enableRegexMiniMax,     toggle: () => setEnableRegexMiniMax(v => !v) },
          ];
          const allSvcs = [...emailServices, ...aiServices];
          const emailActiveCount = emailServices.filter(s => s.on).length;
          const aiActiveCount    = aiServices.filter(s => s.on).length;
          const activeCount      = emailActiveCount + aiActiveCount;
          const setAll = (v: boolean) => {
            [setEnableRegexAWS,setEnableRegexSendGrid,setEnableRegexBrevo,setEnableRegexMailJet,setEnableRegexMailGun,setEnableRegexTencent,setEnableRegexStripe,setEnableRegexSMTP,setEnableRegexPostMark,setEnableRegexTwilio,setEnableRegexSparkpost,setEnableRegexAliyun,setEnableRegexOpenAI,setEnableRegexAnthropic,setEnableRegexGoogleAI,setEnableRegexHuggingFace,setEnableRegexGroq,setEnableRegexCohere,setEnableRegexMistral,setEnableRegexAzureOpenAI,setEnableRegexVertexAI,setEnableRegexOpenRouter,setEnableRegexOpenCode,setEnableRegexMiniMax].forEach(fn => fn(v));
          };
          return (
          <motion.div
            className="lm-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowRegexModal(false)}
          >
            <motion.div
              className="lm-panel lm-panel--wide"
              initial={{ scale: 0.96, y: 16, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.96, y: 16, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 320, damping: 28 }}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <div className="lm-header">
                <div className="lm-header-left">
                  <span className="lm-step">2 / 2</span>
                  <div>
                    <p className="lm-label">Target services</p>
                    <h2 className="lm-title">Configure targets</h2>
                  </div>
                </div>
                <div className="lm-header-right">
                  {selectedCheckFile && <span className="lm-file-chip">{selectedCheckFile}</span>}
                  <button type="button" className="lm-close" onClick={() => setShowRegexModal(false)}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                  </button>
                </div>
              </div>

              <div className="lm-svc-controls">
                <span className="lm-svc-count">{activeCount} / {allSvcs.length} targets active</span>
                <div className="lm-svc-ctrl-btns">
                  <button type="button" className="lm-ctrl-btn" onClick={() => setAll(true)}>All</button>
                  <button type="button" className="lm-ctrl-btn" onClick={() => setAll(false)}>None</button>
                </div>
              </div>

              <div className="lm-body lm-body--svc">

                {/* ── Email & SMS accordion ── */}
                <button
                  type="button"
                  className="lm-acc-head"
                  onClick={() => setSvcEmailOpen(v => !v)}
                >
                  <span className="lm-acc-icon">
                    <svg viewBox="0 0 20 20" fill="none"><rect x="2" y="5" width="16" height="11" rx="2" stroke="currentColor" strokeWidth="1.4"/><path d="M2 7l8 5 8-5" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/></svg>
                  </span>
                  <span className="lm-acc-label">Email &amp; SMS</span>
                  <span className="lm-acc-badge">{emailActiveCount}/{emailServices.length}</span>
                  <span className={`lm-acc-arrow${svcEmailOpen ? ' lm-acc-arrow--open' : ''}`}>
                    <svg viewBox="0 0 12 12" fill="none"><path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </span>
                </button>
                {svcEmailOpen && (
                  <div className="lm-acc-body">
                    {emailServices.map(svc => (
                      <button key={svc.label} type="button" className={`lm-row${svc.on ? ' lm-row--on' : ''}`} onClick={svc.toggle}>
                        <span className="lm-row-logo">{SVC_ICONS[svc.label]}</span>
                        <span className="lm-row-name">{svc.label}</span>
                        <span className={`lm-toggle${svc.on ? ' lm-toggle--on' : ''}`}>
                          <span className="lm-toggle-thumb" />
                        </span>
                      </button>
                    ))}
                  </div>
                )}

                {/* ── AI Models accordion ── */}
                <button
                  type="button"
                  className="lm-acc-head"
                  onClick={() => setSvcAiOpen(v => !v)}
                >
                  <span className="lm-acc-icon">
                    <svg viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.4"/><path d="M10 6v4l3 2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </span>
                  <span className="lm-acc-label">AI Models</span>
                  <span className="lm-acc-badge">{aiActiveCount}/{aiServices.length}</span>
                  <span className={`lm-acc-arrow${svcAiOpen ? ' lm-acc-arrow--open' : ''}`}>
                    <svg viewBox="0 0 12 12" fill="none"><path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </span>
                </button>
                {svcAiOpen && (
                  <div className="lm-acc-body">
                    {aiServices.map(svc => (
                      <button key={svc.label} type="button" className={`lm-row${svc.on ? ' lm-row--on' : ''}`} onClick={svc.toggle}>
                        <span className="lm-row-logo">{SVC_ICONS[svc.label]}</span>
                        <span className="lm-row-name">{svc.label}</span>
                        <span className={`lm-toggle${svc.on ? ' lm-toggle--on' : ''}`}>
                          <span className="lm-toggle-thumb" />
                        </span>
                      </button>
                    ))}
                  </div>
                )}

              </div>

              <div className="lm-footer">
                <button type="button" className="lm-btn lm-btn--ghost" onClick={() => { setShowRegexModal(false); setShowFileModal(true); }}>← Back</button>
                <button type="button" className="lm-btn lm-btn--launch" onClick={handleContinueToPrivacy} disabled={loading}>
                  {loading ? (
                    <><span className="lm-btn-spin" />Launching…</>
                  ) : (
                    <>
                      <svg className="lm-launch-icon" viewBox="0 0 20 20" fill="none">
                        <path d="M10 2C10 2 14 4 16 8C18 12 16 17 16 17L10 14L4 17C4 17 2 12 4 8C6 4 10 2 10 2Z" fill="url(#lm-launch-g)" stroke="none"/>
                        <circle cx="10" cy="9" r="2" fill="white" opacity=".9"/>
                        <defs>
                          <linearGradient id="lm-launch-g" x1="4" y1="2" x2="16" y2="18" gradientUnits="userSpaceOnUse">
                            <stop stopColor="#00E87A"/>
                            <stop offset="1" stopColor="#0ea5e9"/>
                          </linearGradient>
                        </defs>
                      </svg>
                      Launch
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
          );
        })()}
      </AnimatePresence>

      {/* Proxy warning */}
      <AnimatePresence>
        {showProxyWarning && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="cracker-modal-overlay"
            onClick={() => setShowProxyWarning(false)}
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              className="cracker-modal max-w-md"
              onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
            >
              <div className="cracker-modal-header">
                <div>
                  <p className="cracker-label text-[#FF3B55]">System alert</p>
                  <h3 className="cracker-heading">No proxy configured</h3>
                </div>
              </div>
              <div className="cracker-modal-body space-y-4">
                <p className="text-sm text-[#a3a3a3]">
                  No proxy configured on this instance ({proxiesCount} entry{proxiesCount !== 1 ? 's' : ''}).
                </p>
                <div className="p-3 rounded-md border border-[#FF3B55]/25 bg-[#FF3B55]/5">
                  <p className="text-xs font-semibold text-[#FF3B55] mb-2 uppercase tracking-wider">Recommendation</p>
                  <ul className="text-xs text-[#a3a3a3] space-y-1">
                    <li>· IP protection against blocks</li>
                    <li>· Reduced rate limiting</li>
                    <li>· Better load distribution</li>
                  </ul>
                </div>
              </div>
              <div className="cracker-modal-footer">
                <button
                  type="button"
                  onClick={() => {
                    setShowProxyWarning(false);
                    setActiveTab?.('config');
                  }}
                  className="cracker-btn cracker-btn--gold flex-1"
                >
                  Configure
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowProxyWarning(false);
                    executeScan();
                  }}
                  className="cracker-btn cracker-btn--ghost flex-1"
                >
                  Continue
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── New Scan Modal (3 étapes) ─────────────────────────────────────── */}
      <AnimatePresence>
        {showNewScanModal && (
          <motion.div
            className="ns-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeNewScanModal}
          >
            <motion.div
              className="ns-panel"
              initial={{ scale: 0.95, y: 16, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.95, y: 16, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 320, damping: 28 }}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="ns-header">
                <div className="ns-header-left">
                  <span className="ns-step-chip">
                    {newScanStep === 1 ? '01' : newScanStep === 2 ? '02' : '03'} / 03
                  </span>
                  <div>
                    <p className="ns-step-label">
                      {newScanStep === 1 ? 'Identification' : newScanStep === 2 ? 'Liste cible' : 'Confirmation'}
                    </p>
                    <h2 className="ns-title">
                      {newScanStep === 1 ? 'Nouveau scan' : newScanStep === 2 ? 'Choisir la liste' : 'Lancer le scan'}
                    </h2>
                  </div>
                </div>
                <button type="button" className="ns-close" onClick={closeNewScanModal}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>

              {/* Step 1 — Nom + Serveur */}
              {newScanStep === 1 && (
                <div className="ns-body">
                  <div className="ns-field">
                    <label className="ns-label">Nom du scan <span className="ns-optional">(optionnel)</span></label>
                    <input
                      className="ns-input"
                      type="text"
                      placeholder="Ex : campagne-oct-2026"
                      value={newScanName}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewScanName(e.target.value)}
                      maxLength={48}
                      autoFocus
                    />
                  </div>
                  <div className="ns-field" ref={nsDropdownRef}>
                    <label className="ns-label">Serveur</label>
                    <div className={`ns-dd${nsDropdownOpen ? ' ns-dd--open' : ''}`}>
                      {/* Trigger */}
                      <button
                        type="button"
                        className="ns-dd-trigger"
                        onClick={() => setNsDropdownOpen(o => !o)}
                        onBlur={(e: React.FocusEvent) => {
                          if (!nsDropdownRef.current?.contains(e.relatedTarget as Node)) setNsDropdownOpen(false);
                        }}
                      >
                        {effectiveVpsList.length === 0 ? (
                          <span className="ns-dd-trigger-label ns-dd-trigger-label--muted">Aucun serveur configuré</span>
                        ) : (() => {
                          const isServer1 = newScanServerIdx === 0;
                          const slotSt = isServer1 ? status : extraSlots[newScanServerIdx - 1]?.status;
                          const connected = !!slotSt;
                          const busy = !!(isServer1 ? status?.scanning : extraSlots[newScanServerIdx - 1]?.status?.scanning);
                          return (
                            <>
                              <span className={`ns-dd-dot${connected && !busy ? ' ns-dd-dot--free' : busy ? ' ns-dd-dot--busy' : ''}`} />
                              <span className="ns-dd-trigger-label">Server {newScanServerIdx + 1}</span>
                              <span className={`ns-dd-status${busy ? ' ns-dd-status--busy' : !connected ? ' ns-dd-status--offline' : ' ns-dd-status--free'}`}>
                                {busy ? 'En cours' : !connected ? 'Hors ligne' : 'Libre'}
                              </span>
                            </>
                          );
                        })()}
                        <svg className={`ns-dd-chevron${nsDropdownOpen ? ' ns-dd-chevron--up' : ''}`} width="10" height="6" viewBox="0 0 10 6" fill="none">
                          <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                      {/* Options panel */}
                      {nsDropdownOpen && (() => {
                        // Toujours afficher au minimum 2 entrées (Server 1 + Server 2)
                        const displayCount = Math.max(effectiveVpsList.length, 2);
                        return (
                          <div className="ns-dd-list">
                            {Array.from({ length: displayCount }, (_, idx) => {
                              const configured = idx < effectiveVpsList.length;
                              const isServer1 = idx === 0;
                              const slotSt = configured ? (isServer1 ? status : extraSlots[idx - 1]?.status) : null;
                              const connected = !!slotSt;
                              const busy = !!(configured && (isServer1 ? status?.scanning : extraSlots[idx - 1]?.status?.scanning));
                              const unavailable = !configured || !connected || busy;
                              return (
                                <button
                                  key={idx}
                                  type="button"
                                  className={`ns-dd-option${newScanServerIdx === idx && !unavailable ? ' ns-dd-option--active' : ''}${unavailable ? ' ns-dd-option--disabled' : ''}`}
                                  onClick={() => {
                                    if (!unavailable) { setNewScanServerIdx(idx); setNsDropdownOpen(false); }
                                  }}
                                  tabIndex={unavailable ? -1 : 0}
                                >
                                  <span className={`ns-dd-dot${connected && !busy ? ' ns-dd-dot--free' : busy ? ' ns-dd-dot--busy' : ''}`} />
                                  <span className="ns-dd-opt-name">Server {idx + 1}</span>
                                  <span className={`ns-dd-status${busy ? ' ns-dd-status--busy' : !configured || !connected ? ' ns-dd-status--offline' : ' ns-dd-status--free'}`}>
                                    {busy ? 'En cours' : !configured ? 'Non configuré' : !connected ? 'Hors ligne' : 'Libre'}
                                  </span>
                                  {newScanServerIdx === idx && !unavailable && (
                                    <svg className="ns-dd-check" width="12" height="9" viewBox="0 0 12 9" fill="none">
                                      <path d="M1 4l3.5 3.5L11 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2 — Liste */}
              {newScanStep === 2 && (
                <div className="ns-body">
                  <label className="ns-label">Fichier de domaines</label>
                  {loadingCheckFiles ? (
                    <div className="ns-empty">
                      <div className="cracker-launch-ring" style={{ width: 24, height: 24, margin: '0 auto 8px' }} />
                      <p>Chargement…</p>
                    </div>
                  ) : checkFiles.length === 0 ? (
                    <div className="ns-empty">
                      <p>Aucun fichier dans le dossier Check.</p>
                      <p className="ns-hint">Importe des domaines depuis l'onglet Domains.</p>
                    </div>
                  ) : (
                    <div className="ns-file-list">
                      {checkFiles.map((file) => {
                        const isSel = selectedCheckFile === file.name;
                        return (
                          <button
                            key={file.name}
                            type="button"
                            className={`ns-file-item${isSel ? ' ns-file-item--active' : ''}`}
                            onClick={() => setSelectedCheckFile(file.name)}
                          >
                            <span className={`ns-radio${isSel ? ' ns-radio--on' : ''}`}>
                              {isSel && <span className="ns-radio-dot" />}
                            </span>
                            <span className="ns-file-name">{file.name}</span>
                            <span className="ns-file-meta">{formatCheckFileSize(file.size)}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Step 3 — Confirmation */}
              {newScanStep === 3 && (
                <div className="ns-body">
                  <div className="ns-confirm-block">
                    <div className="ns-confirm-row">
                      <span className="ns-confirm-key">Scan</span>
                      <span className="ns-confirm-val">{newScanName.trim() || <em>Sans nom</em>}</span>
                    </div>
                    <div className="ns-confirm-row">
                      <span className="ns-confirm-key">Serveur</span>
                      <span className="ns-confirm-val">Server {newScanServerIdx + 1}</span>
                    </div>
                    <div className="ns-confirm-row">
                      <span className="ns-confirm-key">Liste</span>
                      <span className="ns-confirm-val">{selectedCheckFile}</span>
                    </div>
                  </div>
                  {error && <p className="ns-error">{error}</p>}
                </div>
              )}

              {/* Footer */}
              <div className="ns-footer">
                {newScanStep > 1 && (
                  <button type="button" className="ns-btn ns-btn--ghost" onClick={() => setNewScanStep((prev) => Math.max(1, prev - 1) as 1 | 2 | 3)}>
                    ← Retour
                  </button>
                )}
                {newScanStep === 1 && (
                  <>
                    <button type="button" className="ns-btn ns-btn--ghost" onClick={closeNewScanModal}>Annuler</button>
                    <button
                      type="button"
                      className="ns-btn ns-btn--primary"
                      disabled={effectiveVpsList.length === 0}
                      onClick={() => {
                        // Load files from the selected server before showing step 2
                        currentVpsTarget.current = newScanServerIdx;
                        setSelectedCheckFile(null);
                        setCheckFiles([]);
                        loadCheckFiles();
                        setNewScanStep(2);
                      }}
                    >
                      Suivant →
                    </button>
                  </>
                )}
                {newScanStep === 2 && (
                  <button
                    type="button"
                    className="ns-btn ns-btn--primary"
                    disabled={!selectedCheckFile}
                    onClick={() => setNewScanStep(3)}
                  >
                    Suivant →
                  </button>
                )}
                {newScanStep === 3 && (
                  <button
                    type="button"
                    className="ns-btn ns-btn--launch"
                    disabled={loading}
                    onClick={launchNewScan}
                  >
                    {loading ? (
                      <><span className="ns-spinner" /> Lancement…</>
                    ) : (
                      <>
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path d="M1.5 6h9M6.5 2l4 4-4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Lancer le scan
                      </>
                    )}
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CrackerController; 